import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { DatePicker } from 'primeng/datepicker';
import { catchError, forkJoin, of, Observable, Subscription } from 'rxjs';
import { Timesheet, TimesheetEntry, CreateTimesheetDto, CreateTimesheetEntryDto } from '../../models/timesheet.model';
import { Task } from '../../models/task.model';
import { Project } from '../../models/project.model';
import { Month } from '../../models/allocation.model';
import { TimesheetService } from '../../services/timesheet.service';
import { TaskService } from '../../services/task.service';
import { AuthService } from '../../services/auth.service';
import { EmployeeService } from '../../services/employee.service';
import { AllocationService } from '../../services/allocation.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import { Employee } from '../../models/employee.model';
import { Allocation } from '../../models/allocation.model';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  hoursMinutesToDecimalHours as hoursMinutesToDecimalHoursUtil,
  convertWeekHoursToDecimal as convertWeekHoursToDecimalUtil,
  fromDecimalHoursToHM,
  formatHoursMinutes as formatHoursMinutesUtil,
  formatTotalHours as formatTotalHoursUtil,
  formatHoursMinutesFromHM,
  formatTotalHoursFromDecimalArray,
  parseHoursMinutes,
  sumHoursMinutes,
  computeEntryTotalDecimalHours as computeEntryTotalDecimalHoursUtil
} from '../../utils/timesheet-hours.util';

interface TimesheetRow {
  id?: string;
  projectId: string;
  projectName: string;
  taskId: string;
  taskName: string;
  date?: Date;
  week1Hours: number;
  week2Hours: number;
  week3Hours: number;
  week4Hours: number;
  week5Hours: number;
  /** hours only, 0–24 per day, no decimal. */
  entryHours?: number | null;
  /** minutes only, max 2 digits, max value 59, no decimal. */
  entryMinutes?: number | null;
  /** Computed decimal hours = entryHours + entryMinutes/60 (for API and totals). */
  hours?: number | null;
  description?: string;
  descriptionInvalid?: boolean; // set when description > 500 chars (blocks save)
  totalHours: number;
  isNew?: boolean;
  isEditing?: boolean;
  entryMode: 'week' | 'date';
}

@Component({
  selector: 'app-timesheet',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Select,
    InputText,
    ConfirmDialog,
    Tooltip,
    DatePicker,
    Dialog,
  ],
  providers: [ConfirmationService],
  templateUrl: './timesheet.component.html',
  styleUrl: './timesheet.component.scss'
})
export class TimesheetComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  currentTimesheet: Timesheet | null = null;
  timesheetRows: TimesheetRow[] = [];
  
  assignedProjects: Project[] = [];
  allTasks: Task[] = [];
  filteredTasks: Task[] = []; // Tasks filtered by service group
  currentServiceGroupId: string | null = null; // Primary service group (first) for backward compat
  /** All service group IDs for the current user (from employee or allocations). Used to filter tasks dropdown. */
  currentServiceGroupIds: string[] = [];
  
  selectedMonth: Month = Month.January;
  selectedYear: number = new Date().getFullYear();
  /** Used to revert month/year when user cancels "unsaved changes" on filter change. */
  private previousSelectedMonth: Month = Month.January;
  private previousSelectedYear: number = new Date().getFullYear();

  months = [
    { label: 'January', value: Month.January },
    { label: 'February', value: Month.February },
    { label: 'March', value: Month.March },
    { label: 'April', value: Month.April },
    { label: 'May', value: Month.May },
    { label: 'June', value: Month.June },
    { label: 'July', value: Month.July },
    { label: 'August', value: Month.August },
    { label: 'September', value: Month.September },
    { label: 'October', value: Month.October },
    { label: 'November', value: Month.November },
    { label: 'December', value: Month.December }
  ];
  
  years: { label: string; value: number }[] = [];
  
  loading = false;
  saving = false;
  hasUnsavedChanges = false; // For guard: true when an existing row has been edited and not saved
  fieldTouched: Record<string, boolean> = {};

  /** True when at least one already-saved entry (row with id) is being edited inline. Used to show Save button. */
  get hasInlineEdits(): boolean {
    return this.timesheetRows.some(r => !!(r.id && (r.id as string).trim() !== '') && r.isEditing);
  }

  currentEmployeeId = '';
  currentEmployeeName = '';
  currentUserEmail = '';

  /** When true, page was opened with ?employeeId=... (e.g. from Timesheet Report View) — read-only. */
  isViewingAnotherEmployee = false;

  /** True when the previous month's timesheet exists and is not submitted – block adding/saving current month until previous is submitted. */
  previousMonthHasDraft = false;
  /** Label for message e.g. "February 2025". */
  previousMonthDraftLabel = '';

  // Entry mode: 'week' for week-based, 'date' for date-based
  entryMode: 'week' | 'date' = 'date'; // Default to date-based for better UX

  // ── Work Log search / filter bar ──
  filterProject = '';
  filterTaskType = '';
  filterDescription = '';
  filterDate: Date | null = null;

  get filteredTimesheetRows(): TimesheetRow[] {
    const proj = this.filterProject.trim().toLowerCase();
    const task = this.filterTaskType.trim().toLowerCase();
    const desc = this.filterDescription.trim().toLowerCase();
    const date = this.filterDate;

    return this.timesheetRows.filter(row => {
      if (row.isNew) return true;
      if (proj && !(row.projectName || '').toLowerCase().includes(proj)) return false;
      if (task && !(row.taskName || '').toLowerCase().includes(task)) return false;
      if (desc && !(row.description || '').toLowerCase().includes(desc)) return false;
      if (date) {
        if (!row.date) return false;
        const d = row.date instanceof Date ? row.date : new Date(row.date as any);
        if (d.getFullYear() !== date.getFullYear() ||
            d.getMonth() !== date.getMonth() ||
            d.getDate() !== date.getDate()) return false;
      }
      return true;
    });
  }

  get hasActiveSearchFilters(): boolean {
    return !!(this.filterProject.trim() || this.filterTaskType.trim() || this.filterDescription.trim() || this.filterDate);
  }

  clearSearchFilters(): void {
    this.filterProject = '';
    this.filterTaskType = '';
    this.filterDescription = '';
    this.filterDate = null;
  }

  // Pagination properties (UX: default 10 per page)
  rows: number = 10;
  first: number = 0; // First row index
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50, 100];

  // Dialog properties
  showUserGuide: boolean = false;
  showProjectSummaryDialog: boolean = false;
  showCalendarDialog: boolean = false;
  calendarDays: Array<{ date: Date; day: number; hours: number; status: 'red' | 'amber' | 'green' }> = [];
  showBulkEntryDialog: boolean = false;
  bulkEntryDate: Date | null = null;
  /** Regular mode: start and end date (legacy). Kept for getDatesInRange when using selected dates. */
  bulkEntryStartDate: Date | null = null;
  bulkEntryEndDate: Date | null = null;
  /** Regular mode: user selects multiple dates via calendar; entries created for each selected date. */
  bulkEntrySelectedDates: Date[] = []; // used by template for multi-date picker (regular entry)
  bulkEntries: Array<{ projectId: string; projectName: string; taskId: string; taskName: string; entryHours: number; entryMinutes: number; description: string }> = [];
  /** In bulk dialog: 'regular' = project/task rows; holiday/annualLeave/casualLeave/lieuLeave = leave forms */
  bulkDialogEntryMode: 'regular' | 'holiday' | 'annualLeave' | 'casualLeave' | 'lieuLeave' = 'regular';

  // Holiday / Leave entry mode and form state
  leaveEntryMode: 'regular' | 'holiday' | 'annualLeave' | 'casualLeave' | 'lieuLeave' = 'regular';
  leaveHolidayDate: Date | null = null;
  leaveAnnualStartDate: Date | null = null;
  leaveAnnualEndDate: Date | null = null;
  leaveCasualHours: number = 0;
  leaveCasualMinutes: number = 0;
  leaveCasualDate: Date | null = null;
  leaveLieuHours: number = 0;
  leaveLieuMinutes: number = 0;
  leaveLieuDate: Date | null = null;
  
  // Calendar navigation - separate from work log month/year
  calendarViewMonth: Month = Month.January;
  calendarViewYear: number = new Date().getFullYear();

  /** Cached min/max for datepickers (updated when month/year change) so calendar can select all days in month. */
  timesheetMinDate: Date = new Date();
  timesheetMaxDate: Date = new Date();

  private queryParamsSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private timesheetService: TimesheetService,
    private taskService: TaskService,
    private authService: AuthService,
    private employeeService: EmployeeService,
    private allocationService: AllocationService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private yearMonthFilterService: YearMonthFilterService
  ) {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      this.years.push({ label: i.toString(), value: i });
    }
    // Sync from main header service (same year/month as allocation/forecast)
    this.selectedYear = this.yearMonthFilterService.currentYear;
    this.selectedMonth = this.yearMonthFilterService.currentMonth;
  }

  ngOnInit() {
    const user = this.authService.getUser();
    if (!user) return;

    this.currentEmployeeName = user.displayName || user.email || 'User';
    this.currentUserEmail = user.email || '';

    const applyQueryParams = (params: Record<string, string | undefined>) => {
      const viewEmployeeId = params['employeeId'];
      const viewYear = params['year'] != null && params['year'] !== '' ? +params['year'] : null;
      const viewMonth = params['month'] != null && params['month'] !== '' ? +params['month'] : null;

      if (viewEmployeeId && viewYear != null && viewMonth != null) {
        this.isViewingAnotherEmployee = true;
        this.currentEmployeeId = viewEmployeeId;
        this.selectedYear = viewYear;
        this.selectedMonth = viewMonth as Month;
        this.updateTimesheetDateBounds();
        this.loadDataForViewEmployee(viewEmployeeId, viewYear, viewMonth as Month);
      } else {
        this.isViewingAnotherEmployee = false;
        this.selectedYear = this.yearMonthFilterService.currentYear;
        this.selectedMonth = this.yearMonthFilterService.currentMonth;
        this.loadInitialData();
        // Calendar is shown only when user clicks "View Calendar" button
      }
    };

    this.queryParamsSub = this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      applyQueryParams(params);
    });

    // When user changes year/month in main header, reload timesheet
    this.yearMonthFilterService.year
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(y => {
        if (this.isViewingAnotherEmployee) return;
        if (this.selectedYear !== y) {
          this.selectedYear = y;
          this.doLoadMonthYearData();
        }
      });
    this.yearMonthFilterService.month
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(m => {
        if (this.isViewingAnotherEmployee) return;
        if (this.selectedMonth !== m) {
          this.selectedMonth = m;
          this.doLoadMonthYearData();
        }
      });
  }


  loadInitialData() {
    this.updateTimesheetDateBounds();
    // Removed loading state to prevent spinner on refresh
    
    forkJoin({
      tasks: this.taskService.getActiveTasks(),
      allocations: this.allocationService.getAllAllocations(
        this.selectedYear,
        this.selectedMonth
      ).pipe(catchError(() => {
        return of([]);
      })),
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => {
        return of([]);
      }))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.allTasks = result.tasks;
        
        // Find allocations for the current user by matching email
        // First, find employees that match the user's email (exclude employees marked as excluded from timesheet)
        const matchingEmployees = result.employees.filter(
          (emp: any) => emp.email?.toLowerCase() === this.currentUserEmail?.toLowerCase() && emp.isActive && !emp.excludeFromTimesheet
        );
        
        // Get employee IDs from matching employees
        const matchingEmployeeIds = matchingEmployees.map((emp: any) => emp.id);
        
        // Filter allocations by employee IDs or by employee name/email match
        const userAllocations = result.allocations.filter((alloc: Allocation) => {
          // Check if allocation's employeeId matches any of our matching employee IDs
          if (matchingEmployeeIds.includes(alloc.employeeId)) {
            return true;
          }
          // Also check if allocation's employeeName matches user's name (fallback)
          if (this.currentEmployeeName && alloc.employeeName?.toLowerCase() === this.currentEmployeeName.toLowerCase()) {
            return true;
          }
          // Check if employee email matches (if we have employee data in allocation)
          // Note: AllocationDto doesn't include email, but we can check employee records
          const allocEmployee = result.employees.find((emp: any) => emp.id === alloc.employeeId);
          if (allocEmployee && allocEmployee.email?.toLowerCase() === this.currentUserEmail?.toLowerCase()) {
            return true;
          }
          return false;
        });
        
        // Extract employeeId and service group(s) from allocations or employee
        if (userAllocations.length > 0) {
          this.currentEmployeeId = userAllocations[0].employeeId;
          if (userAllocations[0].employeeName) {
            this.currentEmployeeName = userAllocations[0].employeeName;
          }
          // Build service group list: unique IDs from user's allocations + employee's groups if we have the employee
          const allocSgIds = [...new Set(userAllocations.map((a: Allocation) => a.serviceGroupId).filter(Boolean))] as string[];
          const emp = matchingEmployees.find((e: any) => e.id === this.currentEmployeeId);
          const empSgIds = emp && (emp.serviceGroupIds?.length || emp.serviceGroupId)
            ? (emp.serviceGroupIds?.length ? emp.serviceGroupIds : [emp.serviceGroupId])
            : [];
          this.currentServiceGroupIds = empSgIds.length > 0 ? empSgIds : allocSgIds;
          this.currentServiceGroupId = this.currentServiceGroupIds[0] || allocSgIds[0] || null;
        } else if (matchingEmployees.length > 0) {
          this.currentEmployeeId = matchingEmployees[0].id;
          this.currentEmployeeName = matchingEmployees[0].name;
          const emp = matchingEmployees[0];
          this.currentServiceGroupIds = (emp.serviceGroupIds && emp.serviceGroupIds.length > 0)
            ? emp.serviceGroupIds
            : (emp.serviceGroupId ? [emp.serviceGroupId] : []);
          this.currentServiceGroupId = this.currentServiceGroupIds[0] || null;
        } else {
          this.currentServiceGroupId = null;
          this.currentServiceGroupIds = [];
        }
        // Filter tasks so dropdown shows only tasks for the user's service group(s)
        this.filterTasksByServiceGroup();
        
        // Get assigned projects from API (includes allocation projects + "Available to all employees" e.g. Holidays & Leave)
        if (this.currentEmployeeId) {
          forkJoin({
            timesheet: this.timesheetService.getTimesheetByEmployeeMonthYear(
              this.currentEmployeeId,
              this.selectedYear,
              this.selectedMonth
            ).pipe(catchError(() => of(null))),
            assignedProjects: this.timesheetService.getAssignedProjects(
              this.currentEmployeeId,
              this.selectedYear,
              this.selectedMonth
            ).pipe(catchError(() => of([])))
          }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (data) => {
              this.assignedProjects = (data.assignedProjects as Project[]) || [];
              this.currentTimesheet = data.timesheet;
              if (this.currentTimesheet) {
                this.loadTimesheetData();
              } else {
                this.initializeEmptyTimesheet();
              }
              this.checkPreviousMonthSubmitted();
            },
            error: () => {
              this.initializeEmptyTimesheet();
              this.checkPreviousMonthSubmitted();
            }
          });
        } else {
          // No employee ID: build projects from allocations only
          const projectMap = new Map<string, Project>();
          userAllocations.forEach((alloc: Allocation) => {
            if (alloc.projectId && alloc.projectName) {
              const project: Project = {
                id: alloc.projectId,
                name: alloc.projectName,
                managerId: '',
                description: '',
                startDate: new Date().toISOString(),
                isActive: true
              };
              projectMap.set(alloc.projectId, project);
            }
          });
          this.assignedProjects = Array.from(projectMap.values());
        }
        
        // When no employee ID, initialize empty timesheet and optionally show message
        if (!this.currentEmployeeId) {
          this.previousMonthHasDraft = false;
          this.previousMonthDraftLabel = '';
          this.initializeEmptyTimesheet();
          if (this.assignedProjects.length === 0) {
            this.messageService.add({
              severity: 'info',
              summary: 'No Allocations Found',
              detail: 'No project allocations found for your account. You can still add work log entries manually.'
            });
          }
        }
      },
      error: (error) => {
        // Handle 400 Bad Request specifically
        if (error.status === 400) {
          this.messageService.add({
            severity: 'warn',
            summary: 'No Data Available',
            detail: 'No work log data found for the selected period. You can create a new work log entry.'
          });
          // Initialize empty timesheet if no data exists
          this.initializeEmptyTimesheet();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load work log data. Please try again later.'
          });
        }
        // Removed loading state
      }
    });
  }

  /** Load timesheet for a specific employee when opened via ?employeeId=...&year=...&month=... (read-only). */
  loadDataForViewEmployee(employeeId: string, year: number, month: Month): void {
    forkJoin({
      tasks: this.taskService.getActiveTasks(),
      timesheet: this.timesheetService.getTimesheetByEmployeeMonthYear(employeeId, year, month).pipe(
        catchError(() => of(null))
      ),
      assignedProjects: this.timesheetService.getAssignedProjects(employeeId, year, month).pipe(
        catchError(() => of([]))
      ),
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => of([])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.allTasks = result.tasks;
        this.assignedProjects = result.assignedProjects || [];
        this.filteredTasks = this.allTasks;

        const emp = (result.employees as any[]).find((e: any) => e.id === employeeId);
        if (result.timesheet) {
          this.currentTimesheet = result.timesheet;
          this.currentEmployeeName = this.currentTimesheet.employeeName || (emp && emp.name) || 'Employee';
          this.loadTimesheetData();
        } else {
          this.currentTimesheet = null;
          this.currentEmployeeName = (emp && emp.name) || 'Employee';
          this.initializeEmptyTimesheet();
        }
      },
      error: (err) => {
        console.error('Error loading view-employee timesheet:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load timesheet'
        });
        this.initializeEmptyTimesheet();
      }
    });
  }

  /** Parse API date as local date-only to avoid timezone shifting (e.g. "2025-02-02" staying Feb 2). */
  private parseEntryDate(entryDate: string | Date | undefined): Date | undefined {
    if (entryDate == null) return undefined;
    const d = typeof entryDate === 'string' ? new Date(entryDate) : entryDate;
    if (isNaN(d.getTime())) return undefined;
    // Use local date parts so displayed date matches what was saved (no UTC→local shift)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Send date to API at noon local so UTC serialization does not shift to previous day (e.g. 10 stays 10). */
  private toDateOnlyForApi(date: Date | undefined): Date | undefined {
    if (!date) return undefined;
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return undefined;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
  }

  /** Check if previous month has a timesheet that is not submitted; if so, block adding/saving current month. */
  private checkPreviousMonthSubmitted(): void {
    this.previousMonthHasDraft = false;
    this.previousMonthDraftLabel = '';
    if (!this.currentEmployeeId || this.isViewingAnotherEmployee) return;
    const prev = this.getPreviousMonthYear();
    if (!prev) return;
    this.timesheetService.getTimesheetByEmployeeMonthYear(
      this.currentEmployeeId,
      prev.year,
      prev.month
    ).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(() => of(null))
    ).subscribe(prevTimesheet => {
      if (prevTimesheet && !prevTimesheet.isSubmitted) {
        this.previousMonthHasDraft = true;
        const monthName = this.months.find(m => m.value === prev.month)?.label || '';
        this.previousMonthDraftLabel = `${monthName} ${prev.year}`;
      }
    });
  }

  /** Previous calendar month (for "submit previous month first" rule). Returns null if current month is January. */
  private getPreviousMonthYear(): { month: Month; year: number } | null {
    if (this.selectedMonth === Month.January) {
      return { month: Month.December, year: this.selectedYear - 1 };
    }
    return { month: (this.selectedMonth - 1) as Month, year: this.selectedYear };
  }

  /** Convert whole hours + minutes to hours.minutes format for storage (e.g., 1h 40m = 1.40, 3h 50m = 3.50) */
  decimalFromHoursMinutes(h: number, m: number): number {
    const hVal = Math.max(0, Math.min(99, Math.floor(Number(h) || 0)));
    const mVal = Math.max(0, Math.min(59, Math.floor(Number(m) || 0)));
    return hVal + mVal / 100; // Format: hours.minutes (e.g., 1h 40m = 1.40, 3h 50m = 3.50)
  }

  /** Convert decimal hours to hours.minutes format for DB (e.g. 1.5166... -> 1.31). */
  decimalToHoursMinutesFormat(decimalHours: number): number {
    if (decimalHours == null || Number.isNaN(decimalHours) || decimalHours < 0) return 0;
    const { h, m } = fromDecimalHoursToHM(decimalHours);
    return this.decimalFromHoursMinutes(h, m);
  }

  /** Convert hours.minutes to decimal hours – uses shared util (same as Draft/Report). */
  hoursMinutesToDecimalHours(hoursMinutes: number): number {
    return hoursMinutesToDecimalHoursUtil(hoursMinutes);
  }

  /** Split hours.minutes format into whole hours and minutes (e.g., 1.40 = 1h 40m) */
  hoursMinutesFromDecimal(decimalHours: number): { h: number; m: number } {
    const d = Math.max(0, Number(decimalHours) || 0);
    const h = Math.floor(d);
    const m = Math.round((d - h) * 100); // Extract minutes from decimal part (hours.minutes format)
    return { h, m: m >= 60 ? 59 : m };
  }

  loadTimesheetData() {
    if (!this.currentTimesheet) return;
    
    this.timesheetRows = this.currentTimesheet.entries.map(entry => {
      const hasDate = entry.date != null;
      const entryHoursRaw = entry.hours ?? 0;
      // DB stores hours.minutes (e.g. 7.37 = 7h 37min). Parse with same format so grid and summary match.
      const { h, m } = parseHoursMinutes(entryHoursRaw);
      const decimalHours = hasDate ? this.hoursMinutesToDecimalHours(entryHoursRaw) : (entryHoursRaw || 0);

      const week1Raw = entry.week1Hours ?? 0;
      const week2Raw = entry.week2Hours ?? 0;
      const week3Raw = entry.week3Hours ?? 0;
      const week4Raw = entry.week4Hours ?? 0;
      const week5Raw = entry.week5Hours ?? 0;
      const week1Decimal = this.hoursMinutesToDecimalHours(week1Raw);
      const week2Decimal = this.hoursMinutesToDecimalHours(week2Raw);
      const week3Decimal = this.hoursMinutesToDecimalHours(week3Raw);
      const week4Decimal = this.hoursMinutesToDecimalHours(week4Raw);
      const week5Decimal = this.hoursMinutesToDecimalHours(week5Raw);

      let calculatedTotalHours: number;
      if (hasDate) {
        calculatedTotalHours = decimalHours;
      } else {
        calculatedTotalHours = week1Decimal + week2Decimal + week3Decimal + week4Decimal + week5Decimal;
      }

      return {
        id: entry.id,
        projectId: entry.projectId,
        projectName: entry.projectName,
        taskId: entry.taskId,
        taskName: entry.taskName,
        date: this.parseEntryDate(entry.date as string | Date | undefined),
        week1Hours: week1Raw,
        week2Hours: week2Raw,
        week3Hours: week3Raw,
        week4Hours: week4Raw,
        week5Hours: week5Raw,
        entryHours: hasDate ? h : undefined,
        entryMinutes: hasDate ? m : undefined,
        hours: entryHoursRaw,
        description: entry.description,
        totalHours: calculatedTotalHours,
        isNew: false,
        isEditing: false,
        entryMode: hasDate ? 'date' : 'week'
      };
    });
    this.hasUnsavedChanges = false;
    this.previousSelectedMonth = this.selectedMonth;
    this.previousSelectedYear = this.selectedYear;
    this.updateCalendarDays();
  }

  initializeEmptyTimesheet() {
    this.timesheetRows = [];
    this.first = 0;
    this.previousSelectedMonth = this.selectedMonth;
    this.previousSelectedYear = this.selectedYear;
    this.updateCalendarDays();
  }

  onMonthYearChange() {
    if (this.hasUnsavedChanges) {
      this.confirmationService.confirm({
        message: 'Your changes may not be saved. Switch month/year anyway?',
        header: 'Unsaved changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Switch anyway',
        rejectLabel: 'Stay',
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => {
          this.previousSelectedMonth = this.selectedMonth;
          this.previousSelectedYear = this.selectedYear;
          this.hasUnsavedChanges = false;
          this.doLoadMonthYearData();
        },
        reject: () => {
          this.selectedMonth = this.previousSelectedMonth;
          this.selectedYear = this.previousSelectedYear;
        }
      });
      return;
    }
    this.doLoadMonthYearData();
  }

  private doLoadMonthYearData() {
    this.previousSelectedMonth = this.selectedMonth;
    this.previousSelectedYear = this.selectedYear;
    this.yearMonthFilterService.setYear(this.selectedYear);
    this.yearMonthFilterService.setMonth(this.selectedMonth);
    this.updateTimesheetDateBounds();
    if (this.isViewingAnotherEmployee && this.currentEmployeeId) {
      this.loadDataForViewEmployee(this.currentEmployeeId, this.selectedYear, this.selectedMonth);
      setTimeout(() => this.updateCalendarDays(), 100);
    } else {
      this.loadInitialData();
      setTimeout(() => this.updateCalendarDays(), 100);
    }
  }

  /** Filter tasks so the dropdown only shows tasks that belong to the current user's service group(s). */
  filterTasksByServiceGroup() {
    if (this.currentServiceGroupIds.length > 0) {
      const allowedIds = new Set(this.currentServiceGroupIds);
      this.filteredTasks = this.allTasks.filter(task => {
        if (task.serviceGroupIds && task.serviceGroupIds.length > 0) {
          return task.serviceGroupIds.some(sgId => allowedIds.has(sgId));
        }
        return task.serviceGroupId != null && allowedIds.has(task.serviceGroupId);
      });
    } else if (this.currentServiceGroupId) {
      this.filteredTasks = this.allTasks.filter(task => {
        if (task.serviceGroupIds && task.serviceGroupIds.length > 0) {
          return task.serviceGroupIds.includes(this.currentServiceGroupId!);
        }
        return task.serviceGroupId === this.currentServiceGroupId;
      });
    } else {
      this.filteredTasks = this.allTasks;
    }
  }

  /** Project that is available to all employees (e.g. Holidays & Leave). Used for leave entries. */
  get leaveProject(): Project | null {
    const shared = this.assignedProjects.filter(proj => proj.isAvailableToAllEmployees === true);
    if (!shared.length) {
      return null;
    }

    // More than one project can be shared with all employees (e.g. "Holidays & Leave" and
    // "Internal Activities"), so match the leave one by name rather than taking the first —
    // otherwise leave entries could be attached to Internal Activities.
    const byName = shared.find(proj => {
      const name = (proj.name || '').toLowerCase();
      return name.includes('leave') || name.includes('holiday');
    });

    return byName ?? shared[0];
  }

  /** Find a task by name (case-insensitive). Used for Holiday, Annual Leave, Casual Leave, Lieu Leave. */
  getLeaveTaskByName(name: string): Task | null {
    const n = (name || '').trim().toLowerCase();
    if (!n) return null;
    return this.allTasks.find(t => (t.name || '').trim().toLowerCase() === n) ?? null;
  }

  /** Tasks shown in regular mode: exclude Leave/Holiday type (set in Task Management checkbox). */
  get tasksForRegularEntry(): Task[] {
    return this.filteredTasks.filter(t => !(t.isLeaveType === true));
  }

  /** Task options in bulk dialog: regular = regular tasks only; holiday/leave = single matching task so dropdown shows correct label. */
  get bulkDialogTaskOptions(): Task[] {
    if (this.bulkDialogEntryMode === 'regular') {
      return this.tasksForRegularEntry;
    }
    const task =
      this.bulkDialogEntryMode === 'holiday' ? this.getLeaveTaskByName('Holiday') :
      this.bulkDialogEntryMode === 'annualLeave' ? this.getLeaveTaskByName('Annual Leave') :
      this.bulkDialogEntryMode === 'casualLeave' ? this.getLeaveTaskByName('Casual Leave') :
      this.bulkDialogEntryMode === 'lieuLeave' ? this.getLeaveTaskByName('Lieu Leave') :
      null;
    return task ? [task] : [];
  }

  /** Task options for a row: regular-entry tasks only + current row task if not in list (so selection stays visible). */
  getTaskOptionsForRow(row: TimesheetRow): Task[] {
    // On the shared leave project, offer the leave tasks instead of the regular ones —
    // tasksForRegularEntry deliberately excludes them, which left leave rows with a dropdown
    // that could not show or change their own task.
    const leaveProjectId = this.leaveProject?.id;
    if (leaveProjectId && row.projectId === leaveProjectId) {
      const leaveTasks = this.allTasks.filter(t => t.isLeaveType === true);
      if (leaveTasks.length) {
        return leaveTasks;
      }
    }

    const list = [...this.tasksForRegularEntry];
    if (row.taskId && row.taskId.trim() !== '' && !list.some(t => t.id === row.taskId)) {
      list.push({
        id: row.taskId,
        name: row.taskName || 'Selected task',
        isActive: true,
        isLeaveType: false,
        serviceGroupId: undefined
      });
    }
    return list;
  }

  /** Set hasUnsavedChanges only when editing an already-saved (existing) row. Used for guard and Save visibility. */
  markUnsavedIfExistingRow(row?: TimesheetRow) {
    if (row?.id && (row.id as string).trim() !== '') {
      this.hasUnsavedChanges = true;
    }
  }

  /** True when a new row (no id) has all required fields filled for saving. */
  private isNewRowComplete(row: TimesheetRow): boolean {
    if (row.id && (row.id as string).trim() !== '') return false;
    if (!row.projectId || !row.taskId) return false;
    if (row.entryMode === 'date') {
      return !!(row.date && (row.hours ?? 0) > 0);
    }
    const w = (row.week1Hours ?? 0) + (row.week2Hours ?? 0) + (row.week3Hours ?? 0) + (row.week4Hours ?? 0) + (row.week5Hours ?? 0);
    return w > 0;
  }

  /** Schedule a single auto-save when a new row has just become complete (debounced). */
  private autoSaveNewRowSchedule: ReturnType<typeof setTimeout> | null = null;
  private tryAutoSaveNewRow(row: TimesheetRow) {
    if (!this.isNewRowComplete(row)) return;
    if (this.autoSaveNewRowSchedule) clearTimeout(this.autoSaveNewRowSchedule);
    this.autoSaveNewRowSchedule = setTimeout(() => {
      this.autoSaveNewRowSchedule = null;
      if (this.previousMonthHasDraft || this.saving || this.loading) return;
      const stillComplete = this.timesheetRows.some(r => r === row && this.isNewRowComplete(r));
      if (stillComplete) this.saveTimesheet();
    }, 600);
  }

  // When project is changed: update project name only; keep current task so it is not auto-cleared.
  onProjectSelected(projectId: string, row?: TimesheetRow) {
    if (row) {
      const project = this.assignedProjects.find(p => p.id === projectId);
      if (project) {
        row.projectId = projectId;
        row.projectName = project.name;

        // Picking the shared "Holidays & Leave" project pre-fills the Holiday task and a
        // full 8h day, which is what a holiday almost always is. Without this the row was
        // left with an empty task the regular dropdown does not even offer (leave tasks are
        // filtered out of it), so the entry could not be completed from the inline grid.
        // The user can still change the task to another leave type or edit the hours.
        // Only the leave project, not every shared project — "Internal Activities" is also
        // available to all employees and must not be pre-filled with a Holiday task.
        if (this.leaveProject && project.id === this.leaveProject.id) {
          this.applyDefaultLeaveToRow(row);
        }
      }
      this.markUnsavedIfExistingRow(row);
      this.tryAutoSaveNewRow(row);
    }
  }

  /**
   * Pre-fills a row on the shared leave project with the Holiday task and 8h, but only when
   * the user has not already chosen a task or entered hours — so re-selecting the project on
   * a part-filled row never overwrites their input.
   */
  private applyDefaultLeaveToRow(row: TimesheetRow): void {
    const hasTask = !!row.taskId && row.taskId.trim() !== '';
    const hasHours = (row.entryHours || 0) > 0 || (row.entryMinutes || 0) > 0;
    if (hasTask || hasHours) {
      return;
    }

    const holidayTask = this.getLeaveTaskByName('Holiday');
    if (holidayTask) {
      row.taskId = holidayTask.id;
      row.taskName = holidayTask.name;
    }

    row.entryHours = 8;
    row.entryMinutes = 0;
    row.hours = this.decimalFromHoursMinutes(8, 0);
    row.totalHours = this.hoursMinutesToDecimalHours(row.hours);
    if (row.date) {
      this.updateWeekHoursFromDate(row);
    }
  }

  onTaskSelected(taskId: string, row?: TimesheetRow) {
    const task = this.filteredTasks.find(t => t.id === taskId);
    if (task) {
      if (row) {
        row.taskId = taskId;
        row.taskName = task.name;
        this.markUnsavedIfExistingRow(row);
        this.tryAutoSaveNewRow(row);
      }
    }
  }

  /** Create a single timesheet row for leave: project, task, date, and hours (whole hours + minutes). */
  private createLeaveRow(project: Project, task: Task, date: Date, entryHours: number, entryMinutes: number, description?: string): TimesheetRow {
    const h = this.clampEntryHours(entryHours);
    const m = this.clampEntryMinutes(entryMinutes);
    const hoursFormat = this.decimalFromHoursMinutes(h, m);
    const newRow: TimesheetRow = {
      projectId: project.id,
      projectName: project.name,
      taskId: task.id,
      taskName: task.name,
      date: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
      week1Hours: 0,
      week2Hours: 0,
      week3Hours: 0,
      week4Hours: 0,
      week5Hours: 0,
      entryHours: h,
      entryMinutes: m,
      hours: hoursFormat,
      description: description ?? '',
      totalHours: this.hoursMinutesToDecimalHours(hoursFormat),
      isNew: true,
      isEditing: false,
      entryMode: 'date'
    };
    this.updateWeekHoursFromDate(newRow);
    return newRow;
  }

  addHolidayEntry(description?: string): boolean {
    const project = this.leaveProject;
    const task = this.getLeaveTaskByName('Holiday');
    if (!project || !task) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot add Holiday',
        detail: project ? 'Task type "Holiday" not found. Please add it in Task Management.' : 'No project is set as "Available to all employees". Create one in Project Management.'
      });
      return false;
    }
    const date = this.leaveHolidayDate ? new Date(this.leaveHolidayDate) : new Date();
    date.setHours(0, 0, 0, 0);
    if (date < this.timesheetMinDate || date > this.timesheetMaxDate) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid date', detail: 'Date must be within the selected work log month.' });
      return false;
    }
    this.timesheetRows.forEach(r => r.isEditing = false);
    const row = this.createLeaveRow(project, task, date, 8, 0, description ?? '');
    this.timesheetRows.unshift(row);
    this.updateCalendarDays();
    this.saveTimesheet();
    this.messageService.add({ severity: 'success', summary: 'Added', detail: 'Holiday entry added and saved (8 hours).' });
    return true;
  }

  addAnnualLeaveEntries(): boolean {
    const project = this.leaveProject;
    const task = this.getLeaveTaskByName('Annual Leave');
    if (!project || !task) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot add Annual Leave',
        detail: project ? 'Task type "Annual Leave" not found. Please add it in Task Management.' : 'No project is set as "Available to all employees". Create one in Project Management.'
      });
      return false;
    }
    const start = this.leaveAnnualStartDate ? new Date(this.leaveAnnualStartDate) : null;
    const end = this.leaveAnnualEndDate ? new Date(this.leaveAnnualEndDate) : null;
    if (!start || !end || end < start) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid dates', detail: 'Please select both Start and End date (End must be on or after Start).' });
      return false;
    }
    const minD = new Date(this.timesheetMinDate.getFullYear(), this.timesheetMinDate.getMonth(), this.timesheetMinDate.getDate());
    const maxD = new Date(this.timesheetMaxDate.getFullYear(), this.timesheetMaxDate.getMonth(), this.timesheetMaxDate.getDate());
    const startD = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endD = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    if (endD < minD || startD > maxD) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid dates', detail: 'Start and End dates must fall within the selected work log month.' });
      return false;
    }
    const from = startD < minD ? minD : startD;
    const to = endD > maxD ? maxD : endD;
    this.timesheetRows.forEach(r => r.isEditing = false);
    let count = 0;
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const row = this.createLeaveRow(project, task, new Date(d), 8, 0);
      this.timesheetRows.unshift(row);
      count++;
    }
    this.updateCalendarDays();
    this.saveTimesheet();
    this.messageService.add({ severity: 'success', summary: 'Added', detail: `${count} Annual Leave entries added and saved (8 hours each).` });
    return true;
  }

  addCasualLeaveEntry(): boolean {
    const project = this.leaveProject;
    const task = this.getLeaveTaskByName('Casual Leave');
    if (!project || !task) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot add Casual Leave',
        detail: project ? 'Task type "Casual Leave" not found. Please add it in Task Management.' : 'No project is set as "Available to all employees". Create one in Project Management.'
      });
      return false;
    }
    const h = this.clampEntryHours(this.leaveCasualHours);
    const m = this.clampEntryMinutes(this.leaveCasualMinutes);
    const total = this.hoursMinutesToDecimalHours(this.decimalFromHoursMinutes(h, m));
    if (total <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid hours', detail: 'Please enter hours greater than 0.' });
      return false;
    }
    const date = this.leaveCasualDate ? new Date(this.leaveCasualDate) : new Date();
    date.setHours(0, 0, 0, 0);
    if (date < this.timesheetMinDate || date > this.timesheetMaxDate) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid date', detail: 'Date must be within the selected work log month.' });
      return false;
    }
    this.timesheetRows.forEach(r => r.isEditing = false);
    const row = this.createLeaveRow(project, task, date, h, m);
    this.timesheetRows.unshift(row);
    this.updateCalendarDays();
    this.saveTimesheet();
    this.messageService.add({ severity: 'success', summary: 'Added', detail: 'Casual Leave entry added and saved.' });
    return true;
  }

  addLieuLeaveEntry(): boolean {
    const project = this.leaveProject;
    const task = this.getLeaveTaskByName('Lieu Leave');
    if (!project || !task) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot add Lieu Leave',
        detail: project ? 'Task type "Lieu Leave" not found. Please add it in Task Management.' : 'No project is set as "Available to all employees". Create one in Project Management.'
      });
      return false;
    }
    const h = this.clampEntryHours(this.leaveLieuHours);
    const m = this.clampEntryMinutes(this.leaveLieuMinutes);
    const total = this.hoursMinutesToDecimalHours(this.decimalFromHoursMinutes(h, m));
    if (total <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid hours', detail: 'Please enter hours greater than 0.' });
      return false;
    }
    const date = this.leaveLieuDate ? new Date(this.leaveLieuDate) : new Date();
    date.setHours(0, 0, 0, 0);
    if (date < this.timesheetMinDate || date > this.timesheetMaxDate) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid date', detail: 'Date must be within the selected work log month.' });
      return false;
    }
    this.timesheetRows.forEach(r => r.isEditing = false);
    const row = this.createLeaveRow(project, task, date, h, m);
    this.timesheetRows.unshift(row);
    this.updateCalendarDays();
    this.saveTimesheet();
    this.messageService.add({ severity: 'success', summary: 'Added', detail: 'Lieu Leave entry added and saved.' });
    return true;
  }

  /** Add leave from bulk dialog; close dialog on success. */
  bulkDialogAddHoliday() { if (this.addHolidayEntry()) this.closeBulkEntryDialog(); }
  bulkDialogAddAnnualLeave() { if (this.addAnnualLeaveEntries()) this.closeBulkEntryDialog(); }
  bulkDialogAddCasualLeave() { if (this.addCasualLeaveEntry()) this.closeBulkEntryDialog(); }
  bulkDialogAddLieuLeave() { if (this.addLieuLeaveEntry()) this.closeBulkEntryDialog(); }

  addRow() {
    // User has 1 project → add one row in the table (inline editing)
    // User has 2+ projects → open form to add multiple entries at once
    if (this.assignedProjects.length >= 2) {
      this.openBulkEntryDialog();
      return;
    }

    this.timesheetRows.forEach(r => r.isEditing = false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const newRow: TimesheetRow = {
      projectId: this.assignedProjects[0]?.id ?? '',
      projectName: this.assignedProjects[0]?.name ?? '',
      taskId: '',
      taskName: '',
      date: today,
      week1Hours: 0,
      week2Hours: 0,
      week3Hours: 0,
      week4Hours: 0,
      week5Hours: 0,
      entryHours: 0,
      entryMinutes: 0,
      hours: 0,
      description: '',
      totalHours: 0,
      isNew: true,
      isEditing: true,
      entryMode: 'date'
    };

    this.timesheetRows.unshift(newRow);
  }

  /** Table + icon: always add one new row at top for inline entry (no dialog). */
  /** Table + icon click: stop propagation then add one new row at top for inline entry. */
  onAddRowInlineClick(event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    this.addRowInline();
  }

  addRowInline() {
    if (this.isReadOnly || this.loading || this.previousMonthHasDraft) return;
    this.timesheetRows.forEach(r => r.isEditing = false);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstProject = this.assignedProjects[0];
    const newRow: TimesheetRow = {
      projectId: this.assignedProjects.length === 1 ? (firstProject?.id ?? '') : '',
      projectName: this.assignedProjects.length === 1 ? (firstProject?.name ?? '') : '',
      taskId: '',
      taskName: '',
      date: today,
      week1Hours: 0,
      week2Hours: 0,
      week3Hours: 0,
      week4Hours: 0,
      week5Hours: 0,
      entryHours: 0,
      entryMinutes: 0,
      hours: 0,
      description: '',
      totalHours: 0,
      isNew: true,
      isEditing: true,
      entryMode: 'date'
    };
    this.timesheetRows.unshift(newRow);
  }

  openBulkEntryDialog() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.bulkEntryDate = today;
    this.bulkEntryStartDate = new Date(today);
    this.bulkEntryEndDate = new Date(today);
    const minD = this.getTimesheetMonthMinDate();
    const maxD = this.getTimesheetMonthMaxDate();
    this.bulkEntrySelectedDates = (today >= minD && today <= maxD) ? [new Date(today)] : [];
    this.leaveHolidayDate = new Date(today);
    this.leaveAnnualStartDate = new Date(today);
    this.leaveAnnualEndDate = new Date(today);
    this.leaveCasualDate = new Date(today);
    this.leaveLieuDate = new Date(today);
    this.bulkEntries = [];
    this.bulkDialogEntryMode = 'regular';
    // Always start with empty project selection - user must select
    this.bulkEntries.push({
      projectId: '',
      projectName: '',
      taskId: '',
      taskName: '',
      entryHours: 0,
      entryMinutes: 0,
      description: ''
    });
    this.showBulkEntryDialog = true;
    
    // Reset scroll position after dialog opens (scrollable container is .bulk-entries-wrap)
    setTimeout(() => {
      const scrollContainer = document.querySelector('.bulk-entries-wrap');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }, 150);
  }

  /** Open the work log entry form (bulk dialog) with the given date pre-filled. Used when user clicks a date in the calendar. */
  openBulkEntryDialogForDate(date: Date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    this.bulkEntryDate = d;
    this.bulkEntryStartDate = new Date(d);
    this.bulkEntryEndDate = new Date(d);
    this.bulkEntrySelectedDates = [new Date(d)];
    this.leaveHolidayDate = new Date(d);
    this.leaveAnnualStartDate = new Date(d);
    this.leaveAnnualEndDate = new Date(d);
    this.leaveCasualDate = new Date(d);
    this.leaveLieuDate = new Date(d);
    this.bulkEntries = [];
    this.bulkDialogEntryMode = 'regular';
    this.bulkEntries.push({
      projectId: '',
      projectName: '',
      taskId: '',
      taskName: '',
      entryHours: 0,
      entryMinutes: 0,
      description: ''
    });
    this.showBulkEntryDialog = true;
  }

  /** When bulkEntryDate changes, sync to leave date fields if in leave mode. */
  onBulkDateChange(): void {
    if (!this.bulkEntryDate || this.bulkDialogEntryMode === 'regular' || this.bulkDialogEntryMode === 'annualLeave') return;
    const d = new Date(this.bulkEntryDate);
    d.setHours(0, 0, 0, 0);
    if (this.bulkDialogEntryMode === 'holiday') {
      this.leaveHolidayDate = d;
    } else if (this.bulkDialogEntryMode === 'casualLeave') {
      this.leaveCasualDate = d;
    } else if (this.bulkDialogEntryMode === 'lieuLeave') {
      this.leaveLieuDate = d;
    }
  }

  /** When switching to a leave type in bulk dialog, auto-fill project/task/hours and init dates. */
  onBulkDialogEntryModeChange(): void {
    // Ensure we have at least one entry
    if (this.bulkEntries.length === 0) {
      this.bulkEntries.push({
        projectId: '',
        projectName: '',
        taskId: '',
        taskName: '',
        entryHours: 0,
        entryMinutes: 0,
        description: ''
      });
    }

    const entry = this.bulkEntries[0];

    if (this.bulkDialogEntryMode === 'regular') {
      // Reset to empty
      entry.projectId = '';
      entry.projectName = '';
      entry.taskId = '';
      entry.taskName = '';
      entry.entryHours = 0;
      entry.entryMinutes = 0;
      return;
    }

    const project = this.leaveProject;
    if (!project) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Leave project not found',
        detail: 'No project is set as "Available to all employees". Create one in Project Management.'
      });
      return;
    }

    // Auto-fill project (always the leave project)
    entry.projectId = project.id;
    entry.projectName = project.name;

    // Auto-fill task and hours based on leave type
    if (this.bulkDialogEntryMode === 'holiday') {
      const task = this.getLeaveTaskByName('Holiday');
      if (task) {
        entry.taskId = task.id;
        entry.taskName = task.name;
      }
      entry.entryHours = 8;
      entry.entryMinutes = 0;
      // Sync date to leaveHolidayDate for addHolidayEntry()
      if (this.bulkEntryDate) {
        const d = new Date(this.bulkEntryDate);
        d.setHours(0, 0, 0, 0);
        this.leaveHolidayDate = d;
      }
    } else if (this.bulkDialogEntryMode === 'annualLeave') {
      const task = this.getLeaveTaskByName('Annual Leave');
      if (task) {
        entry.taskId = task.id;
        entry.taskName = task.name;
      }
      entry.entryHours = 8;
      entry.entryMinutes = 0;
      // Sync dates for addAnnualLeaveEntries()
      if (this.bulkEntryDate) {
        const d = new Date(this.bulkEntryDate);
        d.setHours(0, 0, 0, 0);
        this.leaveAnnualStartDate = d;
        this.leaveAnnualEndDate = new Date(d);
      }
    } else if (this.bulkDialogEntryMode === 'casualLeave') {
      const task = this.getLeaveTaskByName('Casual Leave');
      if (task) {
        entry.taskId = task.id;
        entry.taskName = task.name;
      }
      entry.entryHours = 0; // User enters hours
      entry.entryMinutes = 0;
      // Sync date for addCasualLeaveEntry()
      if (this.bulkEntryDate) {
        const d = new Date(this.bulkEntryDate);
        d.setHours(0, 0, 0, 0);
        this.leaveCasualDate = d;
      }
    } else if (this.bulkDialogEntryMode === 'lieuLeave') {
      const task = this.getLeaveTaskByName('Lieu Leave');
      if (task) {
        entry.taskId = task.id;
        entry.taskName = task.name;
      }
      entry.entryHours = 0; // User enters hours
      entry.entryMinutes = 0;
      // Sync date for addLieuLeaveEntry()
      if (this.bulkEntryDate) {
        const d = new Date(this.bulkEntryDate);
        d.setHours(0, 0, 0, 0);
        this.leaveLieuDate = d;
      }
    }
  }

  closeBulkEntryDialog() {
    this.showBulkEntryDialog = false;
    this.bulkEntryDate = null;
    this.bulkEntryStartDate = null;
    this.bulkEntryEndDate = null;
    this.bulkEntrySelectedDates = [];
    this.bulkEntries = [];
    // Reset bulk validation state
    this.fieldTouched['bulkDates'] = false;
    this.fieldTouched['bulkDate'] = false;
    this.fieldTouched['bulkHours'] = false;
    this.fieldTouched['bulkEntries'] = false;
  }

  /** Summary text for Select dates: 1 date = full date (e.g. 04-03-2026); multiple = day numbers (e.g. 3, 4, 5). */
  getBulkDatesSummary(): string {
    const dates = this.bulkEntrySelectedDates ?? [];
    if (dates.length === 0) return 'Click calendar to select dates';
    if (dates.length === 1) {
      const d = new Date(dates[0]);
      if (isNaN(d.getTime())) return 'Click calendar to select dates';
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    }
    const days = dates
      .map(d => new Date(d).getDate())
      .filter(d => !isNaN(d))
      .sort((a, b) => a - b);
    return days.join(', ');
  }

  /** All dates between start and end (inclusive), clamped to work log month. Includes weekends and weekdays. */
  getDatesInRange(start: Date, end: Date): Date[] {
    const minD = this.getTimesheetMonthMinDate();
    const maxD = this.getTimesheetMonthMaxDate();
    const s = new Date(Math.max(start.getTime(), minD.getTime()));
    const e = new Date(Math.min(end.getTime(), maxD.getTime()));
    s.setHours(0, 0, 0, 0);
    e.setHours(0, 0, 0, 0);
    const out: Date[] = [];
    const cur = new Date(s);
    while (cur <= e) {
      out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }

  /** Reset Add Entries form to initial state (one empty entry, today's date, regular mode). Keeps dialog open. */
  resetBulkEntryForm() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.bulkEntryDate = today;
    this.bulkEntryStartDate = new Date(today);
    this.bulkEntryEndDate = new Date(today);
    const minD = this.getTimesheetMonthMinDate();
    const maxD = this.getTimesheetMonthMaxDate();
    this.bulkEntrySelectedDates = (today >= minD && today <= maxD) ? [new Date(today)] : [];
    this.leaveHolidayDate = new Date(today);
    this.leaveAnnualStartDate = new Date(today);
    this.leaveAnnualEndDate = new Date(today);
    this.leaveCasualDate = new Date(today);
    this.leaveLieuDate = new Date(today);
    this.bulkEntries = [];
    this.bulkDialogEntryMode = 'regular';
    this.bulkEntries.push({
      projectId: '',
      projectName: '',
      taskId: '',
      taskName: '',
      entryHours: 0,
      entryMinutes: 0,
      description: ''
    });
    setTimeout(() => {
      const scrollContainer = document.querySelector('.bulk-entries-wrap');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }, 100);
  }

  addBulkEntryRow() {
    this.bulkEntries.push({
      projectId: '',
      projectName: '',
      taskId: '',
      taskName: '',
      entryHours: 0,
      entryMinutes: 0,
      description: ''
    });

    // Auto-scroll to the new entry (scrollable container is .bulk-entries-wrap)
    setTimeout(() => {
      const scrollContainer = document.querySelector('.bulk-entries-wrap');
      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 150);
  }

  removeBulkEntryRow(index: number) {
    if (this.bulkEntries.length > 1) {
      this.bulkEntries.splice(index, 1);
    }
  }

  onBulkProjectChange(entry: { projectId: string; projectName: string; taskId: string; taskName: string; entryHours: number; entryMinutes: number; description: string }, projectId: string) {
    const project = this.assignedProjects.find(p => p.id === projectId);
    if (project) {
      entry.projectId = projectId;
      entry.projectName = project.name;
    }
  }

  onBulkTaskChange(entry: { projectId: string; projectName: string; taskId: string; taskName: string; entryHours: number; entryMinutes: number; description: string }, taskId: string) {
    const task = this.filteredTasks.find(t => t.id === taskId);
    if (task) {
      entry.taskId = taskId;
      entry.taskName = task.name;
    }
  }

  saveBulkEntries() {
    // Handle leave types specially
    if (this.bulkDialogEntryMode === 'holiday') {
      // Sync bulkEntryDate to leaveHolidayDate if changed
      if (this.bulkEntryDate) {
        const d = new Date(this.bulkEntryDate);
        d.setHours(0, 0, 0, 0);
        this.leaveHolidayDate = d;
      }
      const holidayDesc = this.bulkEntries[0]?.description.trim() ?? '';
      if (this.addHolidayEntry(holidayDesc)) {
        this.closeBulkEntryDialog();
      }
      return;
    } else if (this.bulkDialogEntryMode === 'annualLeave') {
      // Dates are already synced via ngModel on leaveAnnualStartDate/EndDate
      if (this.addAnnualLeaveEntries()) {
        this.closeBulkEntryDialog();
      }
      return;
    } else if (this.bulkDialogEntryMode === 'casualLeave') {
      // Sync date and hours from form
      if (!this.bulkEntryDate) {
        this.fieldTouched['bulkDate'] = true;
        return;
      }
      const entry = this.bulkEntries[0];
      const h = this.clampEntryHours(entry.entryHours);
      const m = this.clampEntryMinutes(entry.entryMinutes);
      const total = this.hoursMinutesToDecimalHours(this.decimalFromHoursMinutes(h, m));
      if (total <= 0) {
        this.fieldTouched['bulkHours'] = true;
        return;
      }
      const d = new Date(this.bulkEntryDate);
      d.setHours(0, 0, 0, 0);
      this.leaveCasualDate = d;
      this.leaveCasualHours = h;
      this.leaveCasualMinutes = m;
      if (this.addCasualLeaveEntry()) {
        this.closeBulkEntryDialog();
      }
      return;
    } else if (this.bulkDialogEntryMode === 'lieuLeave') {
      // Sync date and hours from form
      if (!this.bulkEntryDate) {
        this.fieldTouched['bulkDate'] = true;
        return;
      }
      const entry = this.bulkEntries[0];
      const h = this.clampEntryHours(entry.entryHours);
      const m = this.clampEntryMinutes(entry.entryMinutes);
      const total = this.hoursMinutesToDecimalHours(this.decimalFromHoursMinutes(h, m));
      if (total <= 0) {
        this.fieldTouched['bulkHours'] = true;
        return;
      }
      const d = new Date(this.bulkEntryDate);
      d.setHours(0, 0, 0, 0);
      this.leaveLieuDate = d;
      this.leaveLieuHours = h;
      this.leaveLieuMinutes = m;
      if (this.addLieuLeaveEntry()) {
        this.closeBulkEntryDialog();
      }
      return;
    }

    // Regular mode: use calendar-selected dates (recurring / multi-date entry)
    const selected = (this.bulkEntrySelectedDates || []).map(d => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    }).filter(d => d >= this.getTimesheetMonthMinDate() && d <= this.getTimesheetMonthMaxDate());
    const datesToUse = selected.length > 0 ? selected : [];

    if (datesToUse.length === 0) {
      this.fieldTouched['bulkDates'] = true;
      return;
    }

    const validEntries = this.bulkEntries.filter(entry => {
      const h = this.clampEntryHours(entry.entryHours);
      const m = this.clampEntryMinutes(entry.entryMinutes);
      const decimalH = this.decimalFromHoursMinutes(h, m);
      return entry.projectId && entry.taskId && decimalH > 0;
    });

    if (validEntries.length === 0) {
      this.fieldTouched['bulkEntries'] = true;
      return;
    }

    const hadOutOfRange = validEntries.some(e =>
      (e.entryHours != null && (e.entryHours < 0 || e.entryHours > 24)) ||
      (e.entryMinutes != null && (e.entryMinutes < 0 || e.entryMinutes > 59))
    );
    if (hadOutOfRange) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Values adjusted',
        detail: 'Hours must be 0–24 and minutes 0–59. Out-of-range values were corrected.',
      });
    }

    const totalNewHoursPerDay = validEntries.reduce((sum, entry) => {
      const h = this.clampEntryHours(entry.entryHours);
      const m = this.clampEntryMinutes(entry.entryMinutes);
      const hoursMinutesFormat = this.decimalFromHoursMinutes(h, m);
      return sum + this.hoursMinutesToDecimalHours(hoursMinutesFormat);
    }, 0);

    for (const day of datesToUse) {
      const existingHoursForDate = this.getTotalHoursForDate(day);
      const totalHoursForDate = existingHoursForDate + totalNewHoursPerDay;
      if (totalHoursForDate > 24) {
        this.messageService.add({
          severity: 'error',
          summary: '24-hour limit exceeded',
          detail: `Maximum 24 hours per day. ${day.toLocaleDateString()} already has ${existingHoursForDate.toFixed(2)} hours. Adding ${totalNewHoursPerDay.toFixed(2)} hours would exceed the limit.`,
        });
        return;
      }
    }

    this.timesheetRows.forEach(r => r.isEditing = false);

    for (const day of datesToUse) {
      validEntries.forEach(entry => {
        const h = this.clampEntryHours(entry.entryHours);
        const m = this.clampEntryMinutes(entry.entryMinutes);
        const decimalH = this.decimalFromHoursMinutes(h, m);
        const newRow: TimesheetRow = {
          projectId: entry.projectId,
          projectName: entry.projectName,
          taskId: entry.taskId,
          taskName: entry.taskName,
          date: day,
          week1Hours: 0,
          week2Hours: 0,
          week3Hours: 0,
          week4Hours: 0,
          week5Hours: 0,
          entryHours: h,
          entryMinutes: m,
          hours: decimalH,
          description: entry.description || '',
          totalHours: this.hoursMinutesToDecimalHours(decimalH),
          isNew: true,
          isEditing: false,
          entryMode: 'date'
        };
        this.updateWeekHoursFromDate(newRow);
        this.timesheetRows.unshift(newRow);
      });
    }

    this.updateCalendarDays();
    this.closeBulkEntryDialog();
    this.saveTimesheet();
  }

  editRowInline(row: TimesheetRow, index: number) {
    // Cancel any other editing rows
    this.timesheetRows.forEach(r => r.isEditing = false);
    row.isEditing = true;
    // Reset field-touched state for this row
    this.fieldTouched['row_' + index + '_projectId'] = false;
    this.fieldTouched['row_' + index + '_taskId'] = false;
    this.fieldTouched['row_' + index + '_date'] = false;
    this.fieldTouched['row_' + index + '_hours'] = false;
    // Tasks are already filtered by service group, no need to change
  }

  cancelEdit(row: TimesheetRow) {
    row.isEditing = false;
    // Reload data to cancel changes
    if (this.currentTimesheet) {
      this.loadTimesheetData();
    } else {
      // For new rows, just reset to initial state
      const originalRow = this.timesheetRows.find(r => r === row);
      if (originalRow && row.isNew) {
        const index = this.timesheetRows.indexOf(row);
        if (index > -1) {
          this.timesheetRows.splice(index, 1);
        }
      }
    }
  }

  saveRowInline(row: TimesheetRow, index: number) {
    // Mark all required fields as touched
    this.fieldTouched['row_' + index + '_projectId'] = true;
    this.fieldTouched['row_' + index + '_taskId'] = true;
    if (row.entryMode === 'date') this.fieldTouched['row_' + index + '_date'] = true;
    this.fieldTouched['row_' + index + '_hours'] = true;

    if (!row.projectId || !row.taskId || (row.entryMode === 'date' && !row.date)) {
      return;
    }

    const totalHours = row.entryMode === 'date'
      ? (row.hours ? this.hoursMinutesToDecimalHours(row.hours) : 0)
      : (this.convertWeekHoursToDecimal(row.week1Hours || 0) + this.convertWeekHoursToDecimal(row.week2Hours || 0) +
          this.convertWeekHoursToDecimal(row.week3Hours || 0) + this.convertWeekHoursToDecimal(row.week4Hours || 0) +
          this.convertWeekHoursToDecimal(row.week5Hours || 0));

    if (totalHours === 0) {
      return;
    }

    row.totalHours = totalHours;
    row.isEditing = false;
    row.isNew = false;
    this.calculateTotals();
    this.updateCalendarDays();
    if (row.id && (row.id as string).trim() !== '') {
      this.hasUnsavedChanges = true;
    } else if (this.isNewRowComplete(row)) {
      this.saveTimesheet();
    }
  }

  // Calculate which week (1-5) a date falls into for the selected month/year
  calculateWeekFromDate(date: Date): number {
    if (!date) return 0;
    
    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 0;
    
    // Check if date is in the selected month/year
    if (dateObj.getFullYear() !== this.selectedYear || 
        (dateObj.getMonth() + 1) !== this.selectedMonth) {
      return 0; // Date is not in the selected month
    }
    
    const dayOfMonth = dateObj.getDate();
    
    // Calculate which week of the month (1-5) based on date ranges
    // Week 1: Days 1-7
    // Week 2: Days 8-14
    // Week 3: Days 15-21
    // Week 4: Days 22-28
    // Week 5: Days 29-31
    if (dayOfMonth >= 1 && dayOfMonth <= 7) return 1;
    if (dayOfMonth >= 8 && dayOfMonth <= 14) return 2;
    if (dayOfMonth >= 15 && dayOfMonth <= 21) return 3;
    if (dayOfMonth >= 22 && dayOfMonth <= 28) return 4;
    if (dayOfMonth >= 29 && dayOfMonth <= 31) return 5;
    
    return 0; // Invalid day
  }

  // Set default date to today when date picker is opened
  onDatePickerShow(row: TimesheetRow) {
    if (!row.date && row.entryMode === 'date') {
      row.date = new Date();
      row.isEditing = true;
    }
  }

  // Auto-populate week hours when date is selected
  onDateChange(row: TimesheetRow) {
    if (row.date && row.entryMode === 'date') {
      // Recalculate week hours if hours are already set
      if (row.hours && row.hours > 0) {
        this.updateWeekHoursFromDate(row);
      } else {
        // Clear week hours if no hours entered
        row.week1Hours = 0;
        row.week2Hours = 0;
        row.week3Hours = 0;
        row.week4Hours = 0;
        row.week5Hours = 0;
        row.totalHours = 0;
      }
    }
    this.markUnsavedIfExistingRow(row);
    this.tryAutoSaveNewRow(row);
    this.calculateTotals();
  }

  // Update week hours based on date and hours. Store the same value (hours.minutes) in the week column as in row.hours.
  updateWeekHoursFromDate(row: TimesheetRow) {
    if (!row.date || row.entryMode !== 'date') return;

    const weekNumber = this.calculateWeekFromDate(row.date);
    const hoursMinutes = row.hours ?? 0; // already hours.minutes (e.g. 7.59 = 7h 59min)
    const hoursDecimal = hoursMinutes > 0 ? this.hoursMinutesToDecimalHours(hoursMinutes) : 0;

    row.week1Hours = 0;
    row.week2Hours = 0;
    row.week3Hours = 0;
    row.week4Hours = 0;
    row.week5Hours = 0;

    if (hoursMinutes > 0 && weekNumber > 0) {
      switch (weekNumber) {
        case 1: row.week1Hours = hoursMinutes; break;
        case 2: row.week2Hours = hoursMinutes; break;
        case 3: row.week3Hours = hoursMinutes; break;
        case 4: row.week4Hours = hoursMinutes; break;
        case 5: row.week5Hours = hoursMinutes; break;
      }
    }

    row.totalHours = hoursDecimal;
  }

  onRowHoursMinutesChange(row: TimesheetRow) {
    if (row.entryMode !== 'date') return;
    const rawH = row.entryHours;
    const rawM = row.entryMinutes;
    
    // Validate: If hours is 24, minutes must be 0
    if (rawH === 24 && rawM !== null && rawM !== undefined && rawM > 0) {
      row.entryMinutes = 0;
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid entry',
        detail: 'Cannot add minutes when hours is 24. Maximum is 24 hours per entry. Minutes have been reset to 0.',
      });
    }
    
    const h = this.clampEntryHours(rawH);
    const m = this.clampEntryMinutes(rawM);
    
    // Ensure minutes is 0 if hours is 24
    const finalM = h === 24 ? 0 : m;
    
    if (rawH != null && rawH !== undefined && !Number.isNaN(Number(rawH)) && (Number(rawH) < 0 || Number(rawH) > 24)) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid hours', detail: 'Hours must be between 0 and 24.' });
    }
    if (rawM != null && rawM !== undefined && !Number.isNaN(Number(rawM)) && (Number(rawM) < 0 || Number(rawM) > 59)) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid minutes', detail: 'Minutes must be between 0 and 59.' });
    }
    
    // Calculate new total hours for this entry (in hours.minutes format for storage)
    const newEntryHoursMinutesFormat = this.decimalFromHoursMinutes(h, finalM); // e.g., 3.50 (use finalM)
    // Convert to decimal hours for 24-hour limit check
    const newEntryHoursDecimal = this.hoursMinutesToDecimalHours(newEntryHoursMinutesFormat); // e.g., 3.8333...
    
    // Check 24-hour limit per day
    if (row.date && newEntryHoursDecimal > 0) {
      const existingHoursForDate = this.getTotalHoursForDate(row.date, row);
      const totalHoursForDate = existingHoursForDate + newEntryHoursDecimal;
      
      if (totalHoursForDate > 24) {
        // Clamp to maximum allowed (24 - existing hours)
        const maxAllowedHours = 24 - existingHoursForDate;
        if (maxAllowedHours <= 0) {
          // No more hours allowed for this date
          row.entryHours = 0;
          row.entryMinutes = 0;
          row.hours = 0;
          row.totalHours = 0;
          this.messageService.add({ 
            severity: 'error', 
            summary: '24-hour limit exceeded', 
            detail: `Maximum 24 hours per day. This date already has ${existingHoursForDate.toFixed(2)} hours. Cannot add more time.`, 

          });
          this.markUnsavedIfExistingRow(row);
          this.calculateTotals();
          this.updateCalendarDays();
          return;
        } else {
          // Clamp to maximum allowed
          const { h: maxH, m: maxM } = this.hoursMinutesFromDecimal(maxAllowedHours);
          row.entryHours = maxH;
          row.entryMinutes = maxM;
          row.hours = this.decimalFromHoursMinutes(maxH, maxM); // Save as hours.minutes format
          row.totalHours = this.hoursMinutesToDecimalHours(row.hours); // Convert to decimal for calculations
          this.messageService.add({ 
            severity: 'warn', 
            summary: '24-hour limit exceeded', 
            detail: `Maximum 24 hours per day. This date already has ${existingHoursForDate.toFixed(2)} hours. Adjusted to ${maxAllowedHours.toFixed(2)} hours.`, 

          });
          if (row.date && row.hours > 0) {
            this.updateWeekHoursFromDate(row); // Sets week columns AND row.totalHours
          } else {
            row.week1Hours = 0;
            row.week2Hours = 0;
            row.week3Hours = 0;
            row.week4Hours = 0;
            row.week5Hours = 0;
            row.totalHours = 0;
          }
          this.markUnsavedIfExistingRow(row);
          this.tryAutoSaveNewRow(row);
          this.calculateTotals();
          this.updateCalendarDays();
          return;
        }
      }
    }
    
    row.entryHours = h;
    row.entryMinutes = finalM; // Use finalM instead of m (ensures 0 when hours is 24)
    row.hours = newEntryHoursMinutesFormat; // Save as hours.minutes format (e.g., 3.50)
    if (row.date && row.hours > 0) {
      this.updateWeekHoursFromDate(row); // This sets week columns AND row.totalHours = sum of week columns
    } else {
      row.week1Hours = 0;
      row.week2Hours = 0;
      row.week3Hours = 0;
      row.week4Hours = 0;
      row.week5Hours = 0;
      row.totalHours = 0;
    }
    this.markUnsavedIfExistingRow(row);
    this.tryAutoSaveNewRow(row);
    this.calculateTotals();
    this.updateCalendarDays();
  }

  clampEntryHours(value: number | null | undefined): number {
    if (value == null || value === undefined || Number.isNaN(value)) return 0;
    const n = Math.floor(Number(value));
    return Math.max(0, Math.min(24, n));
  }

  clampEntryMinutes(value: number | null | undefined): number {
    if (value == null || value === undefined || Number.isNaN(value)) return 0;
    const n = Math.floor(Number(value));
    return Math.max(0, Math.min(59, n));
  }

  /** Calculate total hours for a specific date, optionally excluding a specific row */
  getTotalHoursForDate(date: Date | undefined, excludeRow?: TimesheetRow): number {
    if (!date) return 0;
    
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth(); // 0-indexed
    const day = dateObj.getDate();
    
    return this.timesheetRows.reduce((sum, row) => {
      // Skip excluded row
      if (excludeRow && row === excludeRow) return sum;
      
      // Only count date-based entries
      if (row.entryMode !== 'date' || !row.date) return sum;
      
      const rowDate = new Date(row.date);
      const rowYear = rowDate.getFullYear();
      const rowMonth = rowDate.getMonth(); // 0-indexed
      const rowDay = rowDate.getDate();
      
      // Check if same date
      if (rowYear === year && rowMonth === month && rowDay === day) {
        // Convert hours.minutes format to decimal hours for calculation
        const decimalHours = row.hours ? this.hoursMinutesToDecimalHours(row.hours) : 0;
        return sum + decimalHours;
      }
      
      return sum;
    }, 0);
  }

  /** Prevent decimal point input in time fields */
  preventDecimalInput(event: KeyboardEvent): void {
    // Allow navigation and editing keys
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    
    // Block decimal point, comma, and 'e' (scientific notation), plus/minus signs
    if (event.key === '.' || event.key === ',' || event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-') {
      event.preventDefault();
    }
  }

  /** Block hours > 24: clamp on input so user cannot keep value above 24. */
  clampHoursInput(row: TimesheetRow, event: Event): void {
    const el = event.target as HTMLInputElement;
    let raw = el.value === '' ? null : parseInt(el.value, 10);
    
    // Block decimal values
    if (el.value.includes('.') || el.value.includes(',')) {
      el.value = el.value.replace(/[.,]/g, '');
      raw = el.value === '' ? null : parseInt(el.value, 10);
    }
    
    // Limit to 2 digits max
    if (el.value.length > 2) {
      el.value = el.value.slice(0, 2);
      raw = parseInt(el.value, 10);
    }
    
    const clamped = this.clampEntryHours(Number.isNaN(raw) ? null : raw);
    row.entryHours = clamped;
    el.value = clamped === 0 && (row.entryHours === undefined || row.entryHours === null) ? '' : String(clamped);
    
    // If hours is set to 24, clear minutes
    if (clamped === 24 && row.entryMinutes && row.entryMinutes > 0) {
      row.entryMinutes = 0;
      this.messageService.add({
        severity: 'info',
        summary: 'Minutes cleared',
        detail: 'Minutes cannot be added when hours is 24. Maximum is 24 hours per entry.',
      });
    }
    
    this.onRowHoursMinutesChange(row);
    row.isEditing = true;
  }

  /** Block minutes > 59: clamp on input so user cannot keep value above 59. */
  clampMinutesInput(row: TimesheetRow, event: Event): void {
    const el = event.target as HTMLInputElement;
    let raw = el.value === '' ? null : parseInt(el.value, 10);
    
    // Block decimal values
    if (el.value.includes('.') || el.value.includes(',')) {
      el.value = el.value.replace(/[.,]/g, '');
      raw = el.value === '' ? null : parseInt(el.value, 10);
    }
    
    // Limit to 2 digits max
    if (el.value.length > 2) {
      el.value = el.value.slice(0, 2);
      raw = parseInt(el.value, 10);
    }
    
    // If hours is 24, minutes must be 0
    if (row.entryHours === 24 && raw !== null && raw !== 0) {
      raw = 0;
      el.value = '0';
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid entry',
        detail: 'Cannot add minutes when hours is 24. Maximum is 24 hours per entry.',
      });
    }
    
    const clamped = this.clampEntryMinutes(Number.isNaN(raw) ? null : raw);
    row.entryMinutes = clamped;
    el.value = String(clamped);
    this.onRowHoursMinutesChange(row);
    row.isEditing = true;
  }

  /** Bulk form: clamp hours to 0–24 so value never stays above 24. */
  onBulkHoursChange(entry: { entryHours: number; entryMinutes: number }): void {
    // Ensure only 2 digits and no decimals
    const hoursStr = String(entry.entryHours || '');
    if (hoursStr.includes('.') || hoursStr.includes(',')) {
      entry.entryHours = parseInt(hoursStr.replace(/[.,]/g, ''), 10) || 0;
    }
    if (hoursStr.length > 2) {
      entry.entryHours = parseInt(hoursStr.slice(0, 2), 10) || 0;
    }
    entry.entryHours = this.clampEntryHours(entry.entryHours);
    
    // If hours is set to 24, clear minutes
    if (entry.entryHours === 24 && entry.entryMinutes > 0) {
      entry.entryMinutes = 0;
    }
  }

  /** Bulk form: clamp minutes to 0–59 so value never stays above 59. */
  onBulkMinutesChange(entry: { entryHours: number; entryMinutes: number }): void {
    // Ensure only 2 digits and no decimals
    const minutesStr = String(entry.entryMinutes || '');
    if (minutesStr.includes('.') || minutesStr.includes(',')) {
      entry.entryMinutes = parseInt(minutesStr.replace(/[.,]/g, ''), 10) || 0;
    }
    if (minutesStr.length > 2) {
      entry.entryMinutes = parseInt(minutesStr.slice(0, 2), 10) || 0;
    }
    
    // If hours is 24, minutes must be 0
    if (entry.entryHours === 24 && entry.entryMinutes > 0) {
      entry.entryMinutes = 0;
    }
    
    entry.entryMinutes = this.clampEntryMinutes(entry.entryMinutes);
  }

  /** Bulk form: clamp hours input on typing - enforces 2-digit limit and blocks decimals. */
  clampBulkHoursInput(entry: { entryHours: number; entryMinutes: number }, event: Event): void {
    const el = event.target as HTMLInputElement;
    let raw = el.value === '' ? null : parseInt(el.value, 10);
    
    // Block decimal values
    if (el.value.includes('.') || el.value.includes(',')) {
      el.value = el.value.replace(/[.,]/g, '');
      raw = el.value === '' ? null : parseInt(el.value, 10);
    }
    
    // Limit to 2 digits max
    if (el.value.length > 2) {
      el.value = el.value.slice(0, 2);
      raw = parseInt(el.value, 10);
    }
    
    const clamped = this.clampEntryHours(Number.isNaN(raw) ? null : raw);
    entry.entryHours = clamped;
    el.value = clamped === 0 && (entry.entryHours === undefined || entry.entryHours === null) ? '' : String(clamped);
  }

  /** Bulk form: clamp minutes input on typing - enforces 2-digit limit and blocks decimals. */
  clampBulkMinutesInput(entry: { entryHours: number; entryMinutes: number }, event: Event): void {
    const el = event.target as HTMLInputElement;
    let raw = el.value === '' ? null : parseInt(el.value, 10);
    
    // Block decimal values
    if (el.value.includes('.') || el.value.includes(',')) {
      el.value = el.value.replace(/[.,]/g, '');
      raw = el.value === '' ? null : parseInt(el.value, 10);
    }
    
    // Limit to 2 digits max
    if (el.value.length > 2) {
      el.value = el.value.slice(0, 2);
      raw = parseInt(el.value, 10);
    }
    
    // If hours is 24, minutes must be 0
    if (entry.entryHours === 24 && raw !== null && raw !== 0) {
      raw = 0;
      el.value = '0';
    }
    
    const clamped = this.clampEntryMinutes(Number.isNaN(raw) ? null : raw);
    entry.entryMinutes = clamped;
    el.value = String(clamped);
  }

  /** Convert week hours to decimal – uses shared util (same as Draft/Report). */
  convertWeekHoursToDecimal(weekHours: number): number {
    return convertWeekHoursToDecimalUtil(weekHours);
  }

  /** Format decimal hours as "Xh Ymin" – uses shared util (same as Draft/Report). */
  formatHoursMinutes(decimalHours: number): string {
    return formatHoursMinutesUtil(decimalHours);
  }

  /** Format total (rounds to nearest minute) – uses shared util (same as Draft/Report). */
  formatTotalHours(decimalHours: number): string {
    return formatTotalHoursUtil(decimalHours);
  }

  onRowHoursChange(row: TimesheetRow) {
    if (row.entryMode === 'date') {
      this.onRowHoursMinutesChange(row);
      return;
    }
    // Sum converted decimal hours so row total = Week1+Week2+...+Week5 (e.g. 26h40 + 15h35 = 42h15)
    const w1 = this.convertWeekHoursToDecimal(row.week1Hours || 0);
    const w2 = this.convertWeekHoursToDecimal(row.week2Hours || 0);
    const w3 = this.convertWeekHoursToDecimal(row.week3Hours || 0);
    const w4 = this.convertWeekHoursToDecimal(row.week4Hours || 0);
    const w5 = this.convertWeekHoursToDecimal(row.week5Hours || 0);
    row.totalHours = Math.round((w1 + w2 + w3 + w4 + w5) * 100) / 100;
    this.markUnsavedIfExistingRow(row);
    this.tryAutoSaveNewRow(row);
    this.calculateTotals();
    this.updateCalendarDays();
  }

  getMonthName(): string {
    const month = this.months.find(m => m.value === this.selectedMonth);
    return month ? month.label : 'this month';
  }

  getCalendarMonthName(): string {
    const month = this.months.find(m => m.value === this.calendarViewMonth);
    return month ? month.label : 'this month';
  }

  /** Update cached min/max for datepickers so only the selected month is selectable. Call when selectedMonth/Year change. */
  private updateTimesheetDateBounds(): void {
    const y = this.selectedYear;
    const m = this.selectedMonth;
    if (!y || !m) return;
    this.timesheetMinDate = new Date(y, m - 1, 1, 0, 0, 0, 0);
    this.timesheetMaxDate = new Date(y, m, 0, 23, 59, 59, 999);
  }

  /** First day of selected work log month – date picker min (calendar only allows this month). */
  getTimesheetMonthMinDate(): Date {
    return this.timesheetMinDate;
  }

  /** Last day of selected work log month – date picker max (calendar only allows this month). */
  getTimesheetMonthMaxDate(): Date {
    return this.timesheetMaxDate;
  }

  // IMPORTANT: the table renders `filteredTimesheetRows` (a filtered COPY, also re-ordered by
  // p-table sorting), so the template's rowIndex does NOT map to an index in `timesheetRows`.
  // Deleting by index therefore removed the wrong entry whenever a filter or sort was active
  // (commonly the last row). Always resolve the row OBJECT and delete by identity.
  removeRow(row: TimesheetRow) {
    if (!row) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete this work log entry for "${row.projectName}" - "${row.taskName}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const index = this.timesheetRows.indexOf(row);
        if (index === -1) return;

        const deletedLabel = `"${row.projectName}" - "${row.taskName}"`;
        this.timesheetRows.splice(index, 1);
        this.calculateTotals();
        this.hasUnsavedChanges = true;
        this.updateCalendarDays();
        // Persist deletion to server so refresh doesn't bring the entry back.
        // Pass a delete-specific toast — the default "Saved as draft" wording made a
        // successful delete look like it had not been applied.
        this.saveTimesheet(undefined, {
          summary: 'Entry deleted',
          detail: `Work log entry for ${deletedLabel} was deleted. Status stays Draft until you click Submit.`
        });
      }
    });
  }

  calculateTotals() {
    const all = this.timesheetRows.flatMap(row => [
      row.week1Hours || 0, row.week2Hours || 0, row.week3Hours || 0, row.week4Hours || 0, row.week5Hours || 0
    ]);
    const w1 = sumHoursMinutes(this.timesheetRows.map(row => row.week1Hours || 0));
    const w2 = sumHoursMinutes(this.timesheetRows.map(row => row.week2Hours || 0));
    const w3 = sumHoursMinutes(this.timesheetRows.map(row => row.week3Hours || 0));
    const w4 = sumHoursMinutes(this.timesheetRows.map(row => row.week4Hours || 0));
    const w5 = sumHoursMinutes(this.timesheetRows.map(row => row.week5Hours || 0));
    const totalHM = sumHoursMinutes(all);
    return {
      week1: w1.h + w1.m / 60,
      week2: w2.h + w2.m / 60,
      week3: w3.h + w3.m / 60,
      week4: w4.h + w4.m / 60,
      week5: w5.h + w5.m / 60,
      total: totalHM.h + totalHM.m / 60,
      totalHM
    };
  }

  getProjectSummaries() {
    const projectMap = new Map<string, {
      projectId: string;
      projectName: string;
      week1Values: number[];
      week2Values: number[];
      week3Values: number[];
      week4Values: number[];
      week5Values: number[];
    }>();

    // Collect week values in hours.minutes format (same as stored)
    this.timesheetRows.forEach(row => {
      if (!projectMap.has(row.projectId)) {
        projectMap.set(row.projectId, {
          projectId: row.projectId,
          projectName: row.projectName,
          week1Values: [],
          week2Values: [],
          week3Values: [],
          week4Values: [],
          week5Values: []
        });
      }

      const summary = projectMap.get(row.projectId)!;
      summary.week1Values.push(row.week1Hours || 0);
      summary.week2Values.push(row.week2Hours || 0);
      summary.week3Values.push(row.week3Hours || 0);
      summary.week4Values.push(row.week4Hours || 0);
      summary.week5Values.push(row.week5Hours || 0);
    });

    const summaries = Array.from(projectMap.values()).map(summary => {
      const w1 = sumHoursMinutes(summary.week1Values);
      const w2 = sumHoursMinutes(summary.week2Values);
      const w3 = sumHoursMinutes(summary.week3Values);
      const w4 = sumHoursMinutes(summary.week4Values);
      const w5 = sumHoursMinutes(summary.week5Values);
      const totalHM = sumHoursMinutes([...summary.week1Values, ...summary.week2Values, ...summary.week3Values, ...summary.week4Values, ...summary.week5Values]);
      return {
        projectId: summary.projectId,
        projectName: summary.projectName,
        total: totalHM.h + totalHM.m / 60,
        totalHM,
        weeks: [
          { hours: w1.h + w1.m / 60, days: 'Days 1-7' },
          { hours: w2.h + w2.m / 60, days: 'Days 8-14' },
          { hours: w3.h + w3.m / 60, days: 'Days 15-21' },
          { hours: w4.h + w4.m / 60, days: 'Days 22-28' },
          { hours: w5.h + w5.m / 60, days: 'Days 29-31' }
        ]
      };
    });

    return summaries;
  }

  getGrandTotalWeeks() {
    const totals = this.calculateTotals();
    return [
      { hours: totals.week1, days: 'Days 1-7' },
      { hours: totals.week2, days: 'Days 8-14' },
      { hours: totals.week3, days: 'Days 15-21' },
      { hours: totals.week4, days: 'Days 22-28' },
      { hours: totals.week5, days: 'Days 29-31' }
    ];
  }

  /** Get project summaries with grand totals. Grand total = same as grid (one sumHoursMinutes). */
  getProjectSummariesWithTotals() {
    const summaries = this.getProjectSummaries();
    const all = this.timesheetRows.flatMap(r => [r.week1Hours || 0, r.week2Hours || 0, r.week3Hours || 0, r.week4Hours || 0, r.week5Hours || 0]);
    const grandTotalHM = sumHoursMinutes(all);
    const allWeek1 = this.timesheetRows.map(r => r.week1Hours || 0);
    const allWeek2 = this.timesheetRows.map(r => r.week2Hours || 0);
    const allWeek3 = this.timesheetRows.map(r => r.week3Hours || 0);
    const allWeek4 = this.timesheetRows.map(r => r.week4Hours || 0);
    const allWeek5 = this.timesheetRows.map(r => r.week5Hours || 0);
    const gw1 = sumHoursMinutes(allWeek1);
    const gw2 = sumHoursMinutes(allWeek2);
    const gw3 = sumHoursMinutes(allWeek3);
    const gw4 = sumHoursMinutes(allWeek4);
    const gw5 = sumHoursMinutes(allWeek5);
    return {
      summaries,
      grandTotal: grandTotalHM.h + grandTotalHM.m / 60,
      grandTotalHM,
      grandTotalWeeks: {
        week1: gw1.h + gw1.m / 60,
        week2: gw2.h + gw2.m / 60,
        week3: gw3.h + gw3.m / 60,
        week4: gw4.h + gw4.m / 60,
        week5: gw5.h + gw5.m / 60
      }
    };
  }

  /** Formatted project total – use same H+M as summary (no re-sum). */
  getFormattedProjectTotal(projectSummary: { totalHM?: { h: number; m: number }; weeks?: Array<{ hours: number }> }): string {
    if (projectSummary.totalHM) return formatHoursMinutesFromHM(projectSummary.totalHM.h, projectSummary.totalHM.m);
    const arr = (projectSummary.weeks || []).map(w => w.hours);
    return formatTotalHoursFromDecimalArray(arr);
  }

  /** Formatted grand total – same value as grid total row (one sumHoursMinutes). */
  getFormattedGrandTotal(): string {
    const { grandTotalHM } = this.getProjectSummariesWithTotals();
    return formatHoursMinutesFromHM(grandTotalHM.h, grandTotalHM.m);
  }

  /** Formatted total for Excel – same as grid. */
  getFormattedTotalForExport(): string {
    const totals = this.calculateTotals();
    const hm = (totals as { totalHM?: { h: number; m: number } }).totalHM;
    if (hm) return formatHoursMinutesFromHM(hm.h, hm.m);
    return formatTotalHoursFromDecimalArray([totals.week1, totals.week2, totals.week3, totals.week4, totals.week5]);
  }

  /** @param onComplete Called when save finishes (e.g. for leave-page dialog). success true = saved, false = failed or validation. */
  // `successMessage` lets a caller describe what actually happened. Deleting an entry persists
  // through this same method, and without an override the user saw "Saved as draft" after a
  // delete — which reads as if the deletion did not happen. Defaults to the save wording.
  saveTimesheet(
    onComplete?: (success: boolean) => void,
    successMessage?: { summary: string; detail: string }
  ) {
    const successToast = successMessage ?? {
      summary: 'Saved as draft',
      detail: 'Your entries are saved. Status stays Draft until you click Submit. Click Submit when ready to lock this month.'
    };
    if (this.previousMonthHasDraft) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Submit previous month first',
        detail: `Submit your ${this.previousMonthDraftLabel} work log before saving ${this.getMonthName()} ${this.selectedYear}.`,
        life:1000

      });
      onComplete?.(false);
      return;
    }
    // Allow save with 0 entries when updating existing timesheet (to persist "delete all" or single delete)
    const canSaveEmpty = !!this.currentTimesheet && this.timesheetRows.length === 0;
    if (this.timesheetRows.length === 0 && !canSaveEmpty) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Entries',
        detail: 'Please add at least one work log entry'
      });
      onComplete?.(false);
      return;
    }

    // If no employee ID found, try to find/create one first
    if (!this.currentEmployeeId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Employee Not Found',
        detail: 'Unable to identify your employee record. Please contact your administrator to set up your employee profile.'
      });
      onComplete?.(false);
      return;
    }

    // Guard: description must not exceed 500 chars (server rejects the WHOLE batch otherwise,
    // silently losing every row). Flag the offending row(s) so the user can fix the right one.
    const overLimit = this.timesheetRows
      .map((row, i) => ({ row, i }))
      .filter(x => (x.row.description?.length || 0) > 500);
    if (overLimit.length > 0) {
      overLimit.forEach(x => (x.row.descriptionInvalid = true));
      const rows = overLimit.map(x => `row ${x.i + 1}`).join(', ');
      this.messageService.add({
        severity: 'warn',
        summary: 'Description too long',
        detail: `Description cannot exceed 500 characters (${rows}). Shorten it and save again — otherwise nothing is saved.`,
        life: 6000
      });
      onComplete?.(false);
      return;
    }

    this.saving = true;

    // Ensure all date-based entries have week hours calculated before saving
    this.timesheetRows.forEach(row => {
      if (row.entryMode === 'date' && row.date && row.hours && row.hours > 0) {
        this.updateWeekHoursFromDate(row);
      }
    });

    const entries: CreateTimesheetEntryDto[] = this.timesheetRows.map(row => {
      // For date-based entries, ensure week hours are calculated
      let week1 = row.week1Hours || 0;
      let week2 = row.week2Hours || 0;
      let week3 = row.week3Hours || 0;
      let week4 = row.week4Hours || 0;
      let week5 = row.week5Hours || 0;
      
      if (row.entryMode === 'date' && row.date && row.hours && row.hours > 0) {
        const weekNumber = this.calculateWeekFromDate(row.date);
        const hoursFormat = row.hours; // hours.minutes (e.g. 1.31)
        week1 = weekNumber === 1 ? hoursFormat : 0;
        week2 = weekNumber === 2 ? hoursFormat : 0;
        week3 = weekNumber === 3 ? hoursFormat : 0;
        week4 = weekNumber === 4 ? hoursFormat : 0;
        week5 = weekNumber === 5 ? hoursFormat : 0;
      } else if (row.entryMode === 'week') {
        week1 = row.week1Hours || 0;
        week2 = row.week2Hours || 0;
        week3 = row.week3Hours || 0;
        week4 = row.week4Hours || 0;
        week5 = row.week5Hours || 0;
      }
      
      // Save Hours and week columns in hours.minutes format (e.g. 1.31 = 1 hour 31 min)
      const hoursToSave = row.entryMode === 'date' ? (row.hours || 0) : 0;
      return {
        projectId: row.projectId,
        taskId: row.taskId,
        date: row.entryMode === 'date' ? this.toDateOnlyForApi(row.date) : undefined,
        week1Hours: week1,
        week2Hours: week2,
        week3Hours: week3,
        week4Hours: week4,
        week5Hours: week5,
        hours: hoursToSave,
        description: row.description || ''
      };
    });

    if (this.currentTimesheet) {
      // Only include complete rows (API requires valid ProjectId, TaskId, and hours)
      const rowsToSave = this.timesheetRows.filter(row =>
        row.projectId && row.taskId &&
        (row.entryMode === 'week' ? (row.week1Hours || 0) + (row.week2Hours || 0) + (row.week3Hours || 0) + (row.week4Hours || 0) + (row.week5Hours || 0) > 0
          : (row.date && (row.hours ?? 0) > 0))
      );

      const entries = rowsToSave.map((row) => {
        let week1 = row.week1Hours || 0;
        let week2 = row.week2Hours || 0;
        let week3 = row.week3Hours || 0;
        let week4 = row.week4Hours || 0;
        let week5 = row.week5Hours || 0;

        if (row.entryMode === 'date' && row.date && row.hours && row.hours > 0) {
          const weekNumber = this.calculateWeekFromDate(row.date);
          const hoursFormat = row.hours; // hours.minutes (e.g. 1.31)
          week1 = weekNumber === 1 ? hoursFormat : 0;
          week2 = weekNumber === 2 ? hoursFormat : 0;
          week3 = weekNumber === 3 ? hoursFormat : 0;
          week4 = weekNumber === 4 ? hoursFormat : 0;
          week5 = weekNumber === 5 ? hoursFormat : 0;
        }

        const dateValue = row.entryMode === 'date' ? this.toDateOnlyForApi(row.date) : undefined;
        // Save Hours and week columns in hours.minutes format (e.g. 1.31 = 1 hour 31 min)
        const hoursVal = row.entryMode === 'date' ? (row.hours || 0) : 0;
        if (row.entryMode === 'week') {
          week1 = row.week1Hours || 0;
          week2 = row.week2Hours || 0;
          week3 = row.week3Hours || 0;
          week4 = row.week4Hours || 0;
          week5 = row.week5Hours || 0;
        }
        const totalHoursVal = row.entryMode === 'date'
          ? (hoursVal ? this.hoursMinutesToDecimalHours(hoursVal) : 0)
          : (this.hoursMinutesToDecimalHours(week1) + this.hoursMinutesToDecimalHours(week2) + this.hoursMinutesToDecimalHours(week3) + this.hoursMinutesToDecimalHours(week4) + this.hoursMinutesToDecimalHours(week5));
        return {
          id: row.id && row.id.trim() !== '' ? row.id : '00000000-0000-0000-0000-000000000000',
          timesheetId: this.currentTimesheet!.id,
          projectId: row.projectId,
          projectName: row.projectName || '',
          taskId: row.taskId,
          taskName: row.taskName || '',
          date: dateValue ?? undefined,
          week1Hours: week1,
          week2Hours: week2,
          week3Hours: week3,
          week4Hours: week4,
          week5Hours: week5,
          hours: hoursVal,
          description: row.description ?? '',
          totalHours: totalHoursVal
        };
      });

      // Build payload explicitly so API receives correct types (number for month/year, valid Guids)
      const updatedTimesheet: Timesheet = {
        id: this.currentTimesheet.id,
        employeeId: this.currentTimesheet.employeeId,
        employeeName: this.currentTimesheet.employeeName || '',
        month: Number(this.currentTimesheet.month) as Month,
        year: Number(this.currentTimesheet.year),
        comments: this.currentTimesheet.comments ?? undefined,
        isSubmitted: !!this.currentTimesheet.isSubmitted,
        submittedDate: this.currentTimesheet.submittedDate,
        isApproved: !!this.currentTimesheet.isApproved,
        approvedByEmployeeId: this.currentTimesheet.approvedByEmployeeId ?? undefined,
        approvedByEmployeeName: this.currentTimesheet.approvedByEmployeeName ?? undefined,
        approvedDate: this.currentTimesheet.approvedDate,
        entries
      };

      this.timesheetService.updateTimesheet(this.currentTimesheet.id, updatedTimesheet).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (savedTimesheet) => {
          this.currentTimesheet = savedTimesheet;
          this.hasUnsavedChanges = false;
          // Reload work log data to show updated week values
          this.loadTimesheetData();
          this.messageService.add({
            severity: 'success',
            summary: successToast.summary,
            detail: successToast.detail,

          });
          this.saving = false;
          onComplete?.(true);
        },
        error: (error) => {
          const detail = this.getTimesheetErrorDetail(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail,

          });
          this.saving = false;
          onComplete?.(false);
        }
      });
    } else {
      const newTimesheet: CreateTimesheetDto = {
        employeeId: this.currentEmployeeId,
        month: this.selectedMonth,
        year: this.selectedYear,
        entries: entries
      };

      this.timesheetService.createTimesheet(newTimesheet).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (savedTimesheet) => {
          this.currentTimesheet = savedTimesheet;
          this.hasUnsavedChanges = false;
          // Reload work log data to show updated week values
          this.loadTimesheetData();
          this.messageService.add({
            severity: 'success',
            summary: successToast.summary,
            detail: successToast.detail,

          });
          this.saving = false;
          onComplete?.(true);
        },
        error: (error) => {
          const detail = this.getTimesheetErrorDetail(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail,

          });
          this.saving = false;
          onComplete?.(false);
        }
      });
    }
  }

  /**
   * Build a user-friendly error message from a failed timesheet save.
   * Handles, in priority order:
   *   1. Our API's structured ErrorResponse (validationErrors / details / message)
   *   2. ASP.NET's native model-binding ValidationProblemDetails (the `errors` object)
   *   3. Transport/status-only failures (no JSON body) — mapped from the HTTP status
   * Only falls back to the generic text when nothing else is available.
   */
  private getTimesheetErrorDetail(error: any): string {
    const body = error?.error;

    // 1. Our API's structured validation errors (BadRequestException.ValidationErrors)
    const structured = this.flattenErrorDict(body?.validationErrors);
    if (structured) return structured;

    // 2. ASP.NET native model-binding errors use `errors`, not `validationErrors`
    const native = this.flattenErrorDict(body?.errors);
    if (native) return native;

    // 3. Explicit server-provided message
    if (body?.details) return body.details;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string' && body.trim()) return body;

    // 4. No JSON body — derive a meaningful message from the HTTP status (auth/timeout/network).
    switch (error?.status) {
      case 0:   return 'Cannot reach the server. Check your connection and try again.';
      case 401: return 'Your session has expired. Please sign in again and retry.';
      case 403: return 'You do not have permission to save this timesheet.';
      case 409: return 'This timesheet was changed elsewhere. Refresh the page and try again.';
      case 422: return 'This timesheet cannot be saved in its current state (it may already be submitted or approved).';
      case 408:
      case 504: return 'The server took too long to respond. Please try again.';
      case 500:
      case 502:
      case 503: return 'The server encountered an error. Please try again shortly or contact support.';
      default:  return 'Failed to save work log. Please check your entries and try again.';
    }
  }

  /** Flattens a { field: string[] } validation-error dictionary into a single sentence, or null if empty. */
  private flattenErrorDict(dict: any): string | null {
    if (!dict || typeof dict !== 'object') return null;
    const messages: string[] = [];
    Object.keys(dict).forEach(key => {
      const vals = dict[key];
      if (Array.isArray(vals)) vals.forEach((v: string) => messages.push(v));
      else if (typeof vals === 'string') messages.push(vals);
    });
    return messages.length ? messages.join('. ') : null;
  }

  submitTimesheet() {
    if (!this.currentTimesheet) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Save First',
        detail: 'Please save the work log before submitting'
      });
      return;
    }

    if (this.timesheetRows.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Entries',
        detail: 'Please add at least one work log entry before submitting'
      });
      return;
    }

    this.confirmationService.confirm({
      message: 'Once you submit this work log, you will NOT be able to edit or change any data for this month. This action is final. Are you sure you want to submit?',
      header: 'Submit Work Log – No Further Edits',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Submit',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.saving = true;
        this.timesheetService.submitTimesheet(this.currentTimesheet!.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (submittedTimesheet) => {
            this.currentTimesheet = submittedTimesheet;
            this.hasUnsavedChanges = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Work Log Submitted',
              detail: 'This timesheet is now locked. You cannot edit or add entries for this month.',
            });
            // Reload data to reflect submitted status and disable editing
            this.loadInitialData();
            this.saving = false;
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to submit work log. Please try again.'
            });
            this.saving = false;
          }
        });
      },
      reject: () => {
        // User cancelled
      }
    });
  }

  get isReadOnly(): boolean {
    return this.isViewingAnotherEmployee ||
      !!this.currentTimesheet?.isSubmitted ||
      !!this.currentTimesheet?.isApproved ||
      false;
  }

  get canSubmit(): boolean {
    return !this.isViewingAnotherEmployee &&
      !!this.currentTimesheet &&
      !this.currentTimesheet.isSubmitted &&
      !this.currentTimesheet.isApproved &&
      this.timesheetRows.length > 0;
  }

  /** Unique project count for stats card. */
  get timesheetStatsProjectCount(): number {
    const ids = new Set(this.timesheetRows.map(r => r.projectId).filter(Boolean));
    return ids.size;
  }

  /** Reopen button: Admin can reopen any timesheet; any user can reopen their own if submitted and not yet approved. */
  get canShowReopenButton(): boolean {
    const isSubmittedNotApproved = !!this.currentTimesheet?.isSubmitted && !this.currentTimesheet?.isApproved;
    if (!isSubmittedNotApproved) return false;
    if (this.authService.hasAdminAccess()) return true;
    return !this.isViewingAnotherEmployee;
  }

  reopenTimesheet(): void {
    if (!this.currentTimesheet || !this.canShowReopenButton) return;
    this.confirmationService.confirm({
      message: 'Reopen this work log so it can be edited or entries added again?',
      header: 'Reopen work log',
      icon: 'pi pi-unlock',
      acceptLabel: 'Reopen',
      rejectLabel: 'Cancel',
      accept: () => {
        this.saving = true;
        this.timesheetService.revertToDraft(this.currentTimesheet!.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (reverted) => {
            this.currentTimesheet = reverted;
            this.loadTimesheetData();
            this.saving = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Reopened',
              detail: 'Work log is now editable again. You can add or edit entries and save.'
            });
          },
          error: (err) => {
            this.saving = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Reopen failed',
              detail: err?.error?.details || err?.error?.message || 'Failed to reopen work log.'
            });
          }
        });
      }
    });
  }

  openProjectSummaryDialog() {
    this.showProjectSummaryDialog = true;
  }

  closeProjectSummaryDialog() {
    this.showProjectSummaryDialog = false;
  }

  closeCalendarDialog() {
    this.showCalendarDialog = false;
  }

  openCalendarDialog() {
    // Initialize calendar view to current work log month/year
    this.calendarViewMonth = this.selectedMonth;
    this.calendarViewYear = this.selectedYear;
    this.updateCalendarDays();
    this.showCalendarDialog = true;
  }

  navigateCalendarMonth(direction: number) {
    let newMonth = this.calendarViewMonth + direction;
    let newYear = this.calendarViewYear;
    
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    
    this.calendarViewMonth = newMonth as Month;
    this.calendarViewYear = newYear;
    this.updateCalendarDays();
  }

  // Update calendar days with hours and status
  updateCalendarDays() {
    // Use calendar view month/year if dialog is open, otherwise use selected month/year
    const year = this.showCalendarDialog ? this.calendarViewYear : this.selectedYear;
    const month = (this.showCalendarDialog ? this.calendarViewMonth : this.selectedMonth) - 1; // JavaScript months are 0-indexed
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    this.calendarDays = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Normalize date to local date (ignore time component)
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      // Find all entries for this date
      const entriesForDate = this.timesheetRows.filter(row => {
        if (!row.date) return false;
        const rowDate = new Date(row.date);
        const rowYear = rowDate.getFullYear();
        const rowMonth = rowDate.getMonth(); // 0-indexed
        const rowDay = rowDate.getDate();
        
        // Compare year, month, and day directly (both months are 0-indexed)
        return rowYear === year && rowMonth === month && rowDay === day;
      });
      
      // Calculate total hours for this date - convert from hours.minutes format
      const totalHours = entriesForDate.reduce((sum, row) => {
        const decimalHours = row.hours ? this.hoursMinutesToDecimalHours(row.hours) : 0;
        return sum + decimalHours;
      }, 0);
      
      // Round to whole hours for display (e.g., 23.083333... -> 23, 12.6 -> 13)
      const roundedHours = Math.round(totalHours);
      
      // Determine status (use original totalHours for status calculation to avoid rounding issues)
      let status: 'red' | 'amber' | 'green' = 'red';
      if (totalHours >= 8) {
        status = 'green';
      } else if (totalHours > 0) {
        status = 'amber';
      }
      
      this.calendarDays.push({
        date: date,
        day: day,
        hours: roundedHours,
        status: status
      });
    }
  }

  /** Export timesheet to Excel – same look as Report/Draft: (#, Week, Project, Task Type, Description, Date, Hours, Weekly Total Hours), week merges, Total row. */
  exportTimesheetToExcel() {
    if (!this.timesheetRows || this.timesheetRows.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No work log entries to export'
      });
      return;
    }

    try {
      type ExportRow = { '#': number | string; 'Week': string; 'Project': string; 'Task Type': string; 'Description': string; 'Date': string; 'Hours': string; 'Weekly Total Hours': string };
      const exportData: ExportRow[] = [];
      const merges: Array<{ s: { r: number; c: number }; e: { r: number; c: number } }> = [];
      const mergeRange = (sr: number, sc: number, er: number, ec: number) => ({ s: { r: sr, c: sc }, e: { r: er, c: ec } });
      const WEEK_COL = 1;
      const WEEKLY_TOTAL_COL = 7;

      // Sort by date (asc) so grouping matches Report/Draft; rows without date last
      const sorted = [...this.timesheetRows].sort((a, b) => {
        const aTime = a.date ? new Date(a.date).getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = b.date ? new Date(b.date).getTime() : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      });

      let rowIndex = 0;
      let i = 0;
      while (i < sorted.length) {
        const row = sorted[i];
        const w = row.entryMode === 'date' && row.date ? this.calculateWeekFromDate(row.date) : 0;
        const weekStart = rowIndex;
        const weekRows: TimesheetRow[] = [];
        while (i < sorted.length) {
          const r = sorted[i];
          const rw = r.entryMode === 'date' && r.date ? this.calculateWeekFromDate(r.date) : 0;
          if (rw !== w) break;
          weekRows.push(r);
          i++;
        }
        const weekValues = w >= 1 && w <= 5
          ? weekRows.map((r) => r.entryMode === 'date' && r.hours != null ? r.hours : (r as any)[`week${w}Hours`] ?? 0).filter((v) => v > 0)
          : [];
        const sumHM = weekValues.length > 0 ? sumHoursMinutes(weekValues) : { h: 0, m: 0 };
        const weeklyTotalFormatted = w > 0 ? formatHoursMinutesFromHM(sumHM.h, sumHM.m) : '';
        const weekLabel = w > 0 ? `Week${w}` : '';
        weekRows.forEach((r, idx) => {
          const entryHours = r.entryMode === 'date' && r.hours != null
            ? formatHoursMinutesFromHM(parseHoursMinutes(r.hours).h, parseHoursMinutes(r.hours).m)
            : '';
          exportData.push({
            '#': rowIndex + 1,
            'Week': idx === 0 ? weekLabel : '',
            'Project': r.projectName || '',
            'Task Type': r.taskName || '',
            'Description': r.description || '',
            'Date': r.date ? new Date(r.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '',
            'Hours': entryHours,
            'Weekly Total Hours': idx === 0 ? weeklyTotalFormatted : ''
          });
          rowIndex++;
        });
        if (weekRows.length > 1 && w > 0) {
          merges.push(mergeRange(weekStart + 1, WEEK_COL, rowIndex, WEEK_COL));
          merges.push(mergeRange(weekStart + 1, WEEKLY_TOTAL_COL, rowIndex, WEEKLY_TOTAL_COL));
        }
      }

      const totalRowIndex = rowIndex;
      exportData.push({
        '#': 'Total',
        'Week': '',
        'Project': '',
        'Task Type': '',
        'Description': '',
        'Date': '',
        'Hours': '',
        'Weekly Total Hours': this.getFormattedTotalForExport()
      });
      merges.push(mergeRange(totalRowIndex + 1, 0, totalRowIndex + 1, 6));

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!merges'] = merges;
      worksheet['!cols'] = [
        { wch: 4 }, { wch: 8 }, { wch: 38 }, { wch: 14 }, { wch: 24 }, { wch: 12 }, { wch: 10 }, { wch: 18 }
      ];
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Timesheet': worksheet },
        SheetNames: ['Timesheet']
      };
      const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data: Blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const monthName = this.months.find((m) => m.value === this.selectedMonth)?.label || 'Month';
      const fileName = `Timesheet_${monthName}_${this.selectedYear}_${this.formatDate(new Date())}.xlsx`;
      saveAs(data, fileName);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Work log data exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export work log data to Excel'
      });
    }
  }

  // Export summary to Excel
  exportSummaryToExcel() {
    const summariesWithTotals = this.getProjectSummariesWithTotals();
    const summaries = summariesWithTotals.summaries;
    if (!summaries || summaries.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No summary data to export'
      });
      return;
    }

    try {
      // Prepare data for Excel export
      const exportData: any[] = [];
      
      summaries.forEach(summary => {
        exportData.push({
          'Project': summary.projectName,
          'Week 1 (Days 1-7)': this.formatHoursMinutes(summary.weeks[0].hours),
          'Week 2 (Days 8-14)': this.formatHoursMinutes(summary.weeks[1].hours),
          'Week 3 (Days 15-21)': this.formatHoursMinutes(summary.weeks[2].hours),
          'Week 4 (Days 22-28)': this.formatHoursMinutes(summary.weeks[3].hours),
          'Week 5 (Days 29-31)': this.formatHoursMinutes(summary.weeks[4].hours),
          'Total': this.getFormattedProjectTotal(summary)
        });
      });

      // Add total row: grand total is sum of week totals (ensures consistency with displayed week columns)
      exportData.push({
        'Project': 'TOTAL',
        'Week 1 (Days 1-7)': this.formatHoursMinutes(summariesWithTotals.grandTotalWeeks.week1),
        'Week 2 (Days 8-14)': this.formatHoursMinutes(summariesWithTotals.grandTotalWeeks.week2),
        'Week 3 (Days 15-21)': this.formatHoursMinutes(summariesWithTotals.grandTotalWeeks.week3),
        'Week 4 (Days 22-28)': this.formatHoursMinutes(summariesWithTotals.grandTotalWeeks.week4),
        'Week 5 (Days 29-31)': this.formatHoursMinutes(summariesWithTotals.grandTotalWeeks.week5),
        'Total': this.getFormattedGrandTotal()
      });

      // Create worksheet
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 25 },  // Project
        { wch: 18 },  // Week 1
        { wch: 18 },  // Week 2
        { wch: 18 },  // Week 3
        { wch: 18 },  // Week 4
        { wch: 18 },  // Week 5
        { wch: 12 }   // Total
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Work Log Summary': worksheet },
        SheetNames: ['Work Log Summary']
      };

      // Generate Excel file
      const excelBuffer: any = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      // Create blob and save file
      const data: Blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const monthName = this.months.find(m => m.value === this.selectedMonth)?.label || 'Month';
      const fileName = `Timesheet_Summary_${monthName}_${this.selectedYear}_${this.formatDate(new Date())}.xlsx`;
      saveAs(data, fileName);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Work log summary exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting summary to Excel:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export work log summary to Excel'
      });
    }
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  // Get day name for calendar
  getDayName(date: Date): string {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  }

  // Get first day of month (for calendar grid)
  getFirstDayOfMonth(): number {
    // Use calendar view month/year if dialog is open, otherwise use selected month/year
    const year = this.showCalendarDialog ? this.calendarViewYear : this.selectedYear;
    const month = (this.showCalendarDialog ? this.calendarViewMonth : this.selectedMonth) - 1;
    return new Date(year, month, 1).getDay();
  }

  // Check if a date is a weekend (Saturday or Sunday)
  isWeekend(date: Date): boolean {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
  }

  /** Calendar date clicked: close calendar and open work log entry form with that date pre-filled. */
  addEntryForDate(day: { date: Date; day: number; hours: number; status: 'red' | 'amber' | 'green' }) {
    if (this.isReadOnly) return;
    if (this.previousMonthHasDraft) return;

    this.closeCalendarDialog();
    this.openBulkEntryDialogForDate(day.date);
  }

  ngOnDestroy() {
    this.queryParamsSub?.unsubscribe();
  }

  /** Called by timesheetUnsavedGuard when user tries to navigate away. */
  canDeactivate(): boolean | Observable<boolean> {
    if (!this.hasUnsavedChanges) return true;
    return new Observable<boolean>((subscriber) => {
      this.confirmationService.confirm({
        message: 'Your recent changes may not be saved. Save your work first or leave without saving?',
        header: 'Leave this page?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Save',
        rejectLabel: 'Leave',
        acceptButtonStyleClass: 'p-button-success',
        accept: () => {
          this.saveTimesheet((success) => {
            subscriber.next(success);
            subscriber.complete();
          });
        },
        reject: () => {
          subscriber.next(true);
          subscriber.complete();
        }
      });
    });
  }

}
